import React, { useEffect, useMemo, useState } from 'react';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getRueckmeldungenMenuItems } from '../../config/roleAccess';
import DateInputField from '../../components/DateInputField';
import {
  createFeedbackEntry,
  deleteFeedbackEntry,
  deleteFeedbackAsset,
  getFeedbackAssetBlob,
  getFeedbackCapabilities,
  getFeedbackEntry,
  getFeedbackOptions,
  listFeedbackEntries,
  updateFeedbackDisplayColor,
  updateFeedbackEntry,
  updateFeedbackStatus,
  uploadFeedbackAssets,
} from '../../api/feedback';
import {
  APP_SUPPORT_AREA_OPTIONS,
  APP_SUPPORT_REPORT_KIND_OPTIONS,
  APP_SUPPORT_STATUS_OPTIONS,
  buildAppSupportEditFormState,
  buildAppSupportSubmitPayload,
  formatAppSupportArea,
  formatAppSupportReportKind,
  formatAppSupportStatusColor,
  formatAppSupportStatusLabel,
  getAppSupportStatusStats,
  getInitialAppSupportFormState,
  matchesAppSupportStatusFilter,
} from './appSupportFormShared';
import {
  buildTechniquesEditFormState,
  buildTechniquesSubmitPayload,
  formatTechniquesStatus,
  getInitialTechniquesFormState,
  getTechniquesStatusStats,
  matchesTechniquesStatusFilter,
  TECHNIQUES_STATUS_OPTIONS,
} from './techniquesFormShared';
import {
  buildIncidentEditFormState,
  buildIncidentSubmitPayload,
  FeedbackFileDropzone,
  getInitialIncidentFormState,
  IncidentFeedbackFormFields,
} from './incidentFormShared';
import {
  buildDispositionPdfDocument,
  buildDispositionPdfFilename,
} from '../../components/pdf/feedbackDispositionPdfBuilder';
import {
  buildWindenrettungPdfDocument,
  buildWindenrettungPdfFilename,
} from '../../components/pdf/feedbackWindenrettungPdfBuilder';
import { loadLogbuchPdfBrandingAssets } from '../../components/pdf/logbuchPdfBuilder';
import {
  normalizeExclusiveFilterInput,
  normalizeMultiSelectInput,
  toggleExclusiveFilterValue,
  toggleMultiFilterValue,
} from './windenrettungFilterUtils';
import {
  filterFeedbackEntriesByYears,
  filterIncidentEntries,
  getAvailableFeedbackYears,
} from './feedbackFilterUtils';

pdfMake.addVirtualFileSystem(pdfFonts);

const TOPIC_QUERY_MAP = {
  disposition: 'disposition_hoerett',
  disposition_hoerett: 'disposition_hoerett',
  zwischenfaelle: 'zwischenfaelle',
  zwischenfall: 'zwischenfaelle',
  ars: 'ars',
  app_support: 'app_support',
  'app-support': 'app_support',
  techniques_working_methods: 'techniques_working_methods',
  'techniken-arbeitsweisen': 'techniques_working_methods',
};

const MENU_PATH_TOPIC_MAP = {
  '/rueckmeldung?type=zwischenfaelle': 'zwischenfaelle',
  '/rueckmeldung?type=disposition': 'disposition_hoerett',
  '/rueckmeldung?type=app-support': 'app_support',
  '/rueckmeldung?type=techniken-arbeitsweisen': 'techniques_working_methods',
  '/rueckmeldung?type=ars': 'ars',
};

const STATUS_OPTIONS = [
  { value: 'open', label: 'Neu', color: 'warning' },
  { value: 'in_review', label: 'In Prüfung', color: 'info' },
  { value: 'resolved', label: 'Abgeschlossen', color: 'success' },
];

function getFormCollapsedStorageKey(topicKey) {
  return `feedback.${String(topicKey || 'zwischenfaelle')}.formCollapsed`;
}

function readInitialFormCollapsed(topicKey) {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.localStorage.getItem(getFormCollapsedStorageKey(topicKey)) === 'true';
  } catch {
    return false;
  }
}

const DISPOSITION_URGENCY_OPTIONS = [
  { value: 'niedrig', label: 'HöRett nicht alarmiert' },
  { value: 'mittel', label: 'HöRett verzögert alarmiert' },
  { value: 'hoch', label: 'Alarmierung HöRett verweigert' },
];

const defaultDispositionForm = {
  eventDate: '',
  title: '',
  description: '',
  urgency: 'mittel',
  deploymentReference: '',
  recommendation: '',
  estimatedTimeAdvantageMinutes: 0,
  linksText: '',
};

const defaultArsForm = {
  eventDate: '',
  title: '',
  locationType: 'nrw',
  winchType: 'winch_operation',
  estimatedTimeAdvantageMinutes: 0,
  description: '',
  linksText: '',
};

const WINDENRETTUNG_LOCATION_OPTIONS = [
  { value: 'nrw', label: 'NRW' },
  { value: 'other', label: 'Andere Örtlichkeit' },
];

const WINDENRETTUNG_WINCH_TYPE_OPTIONS = [
  { value: 'winch_operation', label: 'Windeneinsatz' },
  { value: 'winch_option', label: 'Windenoption' },
];

const WINDENRETTUNG_COLOR_OPTIONS = [
  { value: '', label: 'Keine Farbe', color: 'transparent' },
  { value: '#2E7D32', label: 'Grün', color: '#2E7D32' },
  { value: '#1565C0', label: 'Blau', color: '#1565C0' },
  { value: '#EF6C00', label: 'Orange', color: '#EF6C00' },
  { value: '#8E24AA', label: 'Violett', color: '#8E24AA' },
  { value: '#C62828', label: 'Rot', color: '#C62828' },
  { value: '#546E7A', label: 'Grau', color: '#546E7A' },
];

const TIME_ADVANTAGE_MINUTE_OPTIONS = Array.from({ length: 61 }, (_, index) => index * 10);

function getInitialFormState(topicKey) {
  if (topicKey === 'zwischenfaelle') {
    return getInitialIncidentFormState();
  }

  if (topicKey === 'disposition_hoerett') {
    return defaultDispositionForm;
  }

  if (topicKey === 'app_support') {
    return getInitialAppSupportFormState();
  }

  if (topicKey === 'techniques_working_methods') {
    return getInitialTechniquesFormState();
  }

  return defaultArsForm;
}

function parseTopicFromSearchParams(searchParams) {
  const raw = String(searchParams.get('type') || '').trim().toLowerCase();
  return TOPIC_QUERY_MAP[raw] || 'zwischenfaelle';
}

function parseEntryIdFromSearchParams(searchParams) {
  const raw = Number.parseInt(String(searchParams.get('entry') || ''), 10);
  return Number.isInteger(raw) && raw > 0 ? raw : null;
}

function parseLinksText(value) {
  return String(value || '')
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatDate(value) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString('de-DE');
}

function formatDateTime(value) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString('de-DE');
}

function formatInjuryType(value) {
  if (value === 'with_person_damage') {
    return 'Mit Personenschaden';
  }

  if (value === 'without_person_damage') {
    return 'Ohne Personenschaden';
  }

  return '-';
}

function formatStatusLabel(value) {
  return STATUS_OPTIONS.find((item) => item.value === value)?.label || value || '-';
}

function formatStatusColor(value) {
  return STATUS_OPTIONS.find((item) => item.value === value)?.color || 'default';
}

function formatDispositionUrgency(value) {
  return DISPOSITION_URGENCY_OPTIONS.find((item) => item.value === value)?.label || '-';
}

function formatWindenrettungLocation(value) {
  return WINDENRETTUNG_LOCATION_OPTIONS.find((item) => item.value === value)?.label || '-';
}

function formatWindenrettungWinchType(value) {
  return WINDENRETTUNG_WINCH_TYPE_OPTIONS.find((item) => item.value === value)?.label || '-';
}

function formatWindenrettungColor(value) {
  return WINDENRETTUNG_COLOR_OPTIONS.find((item) => item.value === value)?.label || '-';
}

function formatYearLabel(value) {
  return Number.isInteger(Number(value)) ? String(value) : '-';
}

function getWindenrettungTeaserStats(entries) {
  return {
    nrw: entries.filter((entry) => entry.payload?.locationType === 'nrw').length,
    other: entries.filter((entry) => entry.payload?.locationType === 'other').length,
    winch_operation: entries.filter((entry) => entry.payload?.winchType === 'winch_operation').length,
    winch_option: entries.filter((entry) => entry.payload?.winchType === 'winch_option').length,
  };
}

function getDispositionTeaserStats(entries) {
  return {
    niedrig: entries.filter((entry) => entry.payload?.urgency === 'niedrig').length,
    mittel: entries.filter((entry) => entry.payload?.urgency === 'mittel').length,
    hoch: entries.filter((entry) => entry.payload?.urgency === 'hoch').length,
  };
}

function formatAssetCount(assets = [], kind) {
  return assets.filter((asset) => asset.asset_kind === kind).length;
}

function formatAssetChipLabel(count, singularLabel, pluralLabel) {
  if (count === 1) {
    return `1 ${singularLabel}`;
  }

  return `${count} ${pluralLabel}`;
}

function buildInitialResolutionState(entry = null) {
  return {
    needsFollowUp: entry?.resolution?.needsFollowUp || '',
    noFollowUpReason: entry?.resolution?.noFollowUpReason || '',
    followUpSummary: entry?.resolution?.followUpSummary || '',
    publishToGroup: Boolean(entry?.published_to_group),
  };
}

function isResolutionStateValid(resolutionState) {
  if (!resolutionState?.needsFollowUp) {
    return false;
  }

  if (resolutionState.needsFollowUp === 'no') {
    return Boolean(String(resolutionState.noFollowUpReason || '').trim());
  }

  if (resolutionState.needsFollowUp === 'yes') {
    return Boolean(String(resolutionState.followUpSummary || '').trim());
  }

  return false;
}

function getStatusStats(entries) {
  return {
    open: entries.filter((entry) => entry.status === 'open').length,
    in_review: entries.filter((entry) => entry.status === 'in_review').length,
    resolved: entries.filter((entry) => entry.status === 'resolved').length,
  };
}

function getDisplayColorStats(entries) {
  return entries.reduce((stats, entry) => {
    const colorKey = String(entry.display_color || '').trim();
    stats[colorKey] = (stats[colorKey] || 0) + 1;
    return stats;
  }, {});
}

function getIncidentReporterLabel(entry) {
  const reporterMode = entry?.payload?.reporterMode;
  if (reporterMode === 'anonymous') {
    return 'Anonym';
  }

  if (reporterMode === 'other_named') {
    return entry?.payload?.reporterName || '-';
  }

  return entry?.created_by_name || 'Eintragender';
}

function getFormMeta(topicKey, canEvaluate) {
  if (topicKey === 'zwischenfaelle') {
    return {
      heading: 'Zwischenfall melden',
      helperText: '',
      descriptionLabel: 'Beschreibung des Zwischenfalls',
      linksLabel: 'URLs (eine pro Zeile)',
    };
  }

  if (topicKey === 'disposition_hoerett') {
    return {
      heading: canEvaluate ? 'Dispositionsrückmeldung erfassen und auswerten' : 'Dispositionsrückmeldung erfassen',
      helperText: canEvaluate
        ? 'Die Dispositionsmaske bleibt links verfügbar, während rechts Status und Rückmeldungen bearbeitet werden.'
        : 'Nur Erfassung: die Dispositionsmaske ist auf Breite optimiert, ohne rechte Leerfläche.',
      descriptionLabel: 'Weshalb wurde die HöRett nicht alarmiert / verzögert alarmiert / verweigert?',
      linksLabel: 'Verweise / URLs (eine pro Zeile)',
    };
  }

  if (topicKey === 'app_support') {
    return {
      heading: canEvaluate ? 'App-Support erfassen und auswerten' : 'App-Support melden',
      helperText: canEvaluate
        ? 'Neue Meldungen können links erfasst und rechts direkt beantwortet oder farblich markiert werden.'
        : 'Datum und Titel werden automatisch aus den ersten beiden Antworten erzeugt.',
      descriptionLabel: 'Beschreibung des Anliegens',
      linksLabel: 'Links / URLs (optional, eine pro Zeile)',
    };
  }

  if (topicKey === 'techniques_working_methods') {
    return {
      heading: canEvaluate ? 'Techniken & Arbeitsweisen erfassen und auswerten' : 'Technik oder Arbeitsweise melden',
      helperText: canEvaluate
        ? 'Neue Meldungen können links erfasst und rechts bewertet, ergänzt oder farblich markiert werden.'
        : 'In der Übersicht werden ausschließlich Überschrift, Eingabedatum und Bearbeitungsstatus angezeigt.',
      descriptionLabel: 'Beschreibung der Technik oder Arbeitsweise',
      linksLabel: '',
    };
  }

  return {
    heading: canEvaluate ? 'Windenrettung erfassen und verwalten' : 'Windenrettung erfassen',
    helperText: canEvaluate
      ? 'Die Eingabe bleibt sichtbar, vorhandene Windenrettungs-Einträge können parallel geprüft werden.'
      : 'Nur Erfassung ohne zusätzliche Listenansicht.',
    descriptionLabel: 'Beschreibung',
    linksLabel: 'URLs (eine pro Zeile)',
  };
}

function buildSubmitPayload(topicKey, formState, options) {
  if (topicKey === 'zwischenfaelle') {
    return buildIncidentSubmitPayload(formState, options);
  }

  if (topicKey === 'app_support') {
    return buildAppSupportSubmitPayload(formState);
  }

  if (topicKey === 'techniques_working_methods') {
    return buildTechniquesSubmitPayload(formState);
  }

  const payload = {
    topicKey,
    eventDate: formState.eventDate,
    title: formState.title,
    description: formState.description,
    payload: {
      links: parseLinksText(formState.linksText),
    },
  };

  if (topicKey === 'disposition_hoerett') {
    payload.payload = {
      ...payload.payload,
      urgency: formState.urgency,
      deploymentReference: formState.deploymentReference,
      recommendation: formState.recommendation,
      estimatedTimeAdvantageMinutes: Number(formState.estimatedTimeAdvantageMinutes),
    };
  }

  if (topicKey === 'ars') {
    payload.payload = {
      ...payload.payload,
      locationType: formState.locationType,
      winchType: formState.winchType,
      estimatedTimeAdvantageMinutes: Number(formState.estimatedTimeAdvantageMinutes),
    };
  }

  return payload;
}

function buildEditFormState(entry) {
  const payload = entry.payload || {};

  if (entry.topic_key === 'zwischenfaelle') {
    return buildIncidentEditFormState(entry);
  }

  if (entry.topic_key === 'app_support') {
    return buildAppSupportEditFormState(entry);
  }

  if (entry.topic_key === 'techniques_working_methods') {
    return buildTechniquesEditFormState(entry);
  }

  if (entry.topic_key === 'disposition_hoerett') {
    return {
      eventDate: entry.event_date || '',
      title: entry.title || '',
      description: entry.description || '',
      urgency: payload.urgency || 'mittel',
      deploymentReference: payload.deploymentReference || '',
      recommendation: payload.recommendation || '',
      estimatedTimeAdvantageMinutes: Number.isInteger(Number(payload.estimatedTimeAdvantageMinutes))
        ? Number(payload.estimatedTimeAdvantageMinutes)
        : 0,
      linksText: Array.isArray(payload.links) ? payload.links.join('\n') : '',
    };
  }

  if (entry.topic_key === 'ars') {
    return {
      eventDate: entry.event_date || '',
      title: entry.title || '',
      locationType: payload.locationType || 'nrw',
      winchType: payload.winchType || 'winch_operation',
      estimatedTimeAdvantageMinutes: Number.isInteger(Number(payload.estimatedTimeAdvantageMinutes))
        ? Number(payload.estimatedTimeAdvantageMinutes)
        : 0,
      description: entry.description || '',
      linksText: Array.isArray(payload.links) ? payload.links.join('\n') : '',
    };
  }

  return {
    eventDate: entry.event_date || '',
    title: entry.title || '',
    description: entry.description || '',
    linksText: Array.isArray(payload.links) ? payload.links.join('\n') : '',
  };
}

function RueckmeldungenPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [capabilities, setCapabilities] = useState([]);
  const [options, setOptions] = useState({ entryTypes: [], responsibles: [], techniquesContacts: [] });
  const [entries, setEntries] = useState([]);
  const [sortKey, setSortKey] = useState('newest');
  const [loadingCapabilities, setLoadingCapabilities] = useState(true);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingAssets, setUploadingAssets] = useState(false);
  const [deletingAssetId, setDeletingAssetId] = useState(null);
  const [deletingEntryId, setDeletingEntryId] = useState(null);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [submitDialog, setSubmitDialog] = useState({
    open: false,
    title: '',
    message: '',
    showInjuryWarning: false,
  });
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [detailEntry, setDetailEntry] = useState(null);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [resolutionPdfFiles, setResolutionPdfFiles] = useState([]);
  const [incidentYearFilters, setIncidentYearFilters] = useState([]);
  const [incidentInjuryFilters, setIncidentInjuryFilters] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dispositionYearFilters, setDispositionYearFilters] = useState([]);
  const [dispositionUrgencyFilters, setDispositionUrgencyFilters] = useState([]);
  const [arsYearFilters, setArsYearFilters] = useState([]);
  const [arsLocationFilters, setArsLocationFilters] = useState([]);
  const [arsWinchTypeFilters, setArsWinchTypeFilters] = useState([]);
  const [arsColorFilters, setArsColorFilters] = useState([]);
  const [appSupportColorFilters, setAppSupportColorFilters] = useState([]);
  const [techniquesColorFilters, setTechniquesColorFilters] = useState([]);
  const [exportingDispositionPdf, setExportingDispositionPdf] = useState(false);
  const [exportingWindenrettungPdf, setExportingWindenrettungPdf] = useState(false);
  const [reviewDialog, setReviewDialog] = useState({ open: false, entry: null });
  const [completionDialog, setCompletionDialog] = useState({ open: false, entry: null });
  const [appSupportResponseDialog, setAppSupportResponseDialog] = useState({ open: false, entry: null, responseMessage: '' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, entry: null });
  const [resolutionState, setResolutionState] = useState(() => buildInitialResolutionState());
  const [reopenDialog, setReopenDialog] = useState({ open: false, entry: null, nextStatus: 'open', reopenReason: '' });
  const [assetPreviewUrls, setAssetPreviewUrls] = useState({});
  const [loadingAssetId, setLoadingAssetId] = useState(null);
  const [updatingColorId, setUpdatingColorId] = useState(null);
  const activeTopicKey = parseTopicFromSearchParams(searchParams);
  const requestedEntryId = parseEntryIdFromSearchParams(searchParams);
  const [formCollapsed, setFormCollapsed] = useState(() => readInitialFormCollapsed(activeTopicKey));
  const [formState, setFormState] = useState(() => getInitialFormState(activeTopicKey));

  const activeTopic = useMemo(
    () => capabilities.find((item) => item.key === activeTopicKey) || null,
    [capabilities, activeTopicKey],
  );

  const visibleTopics = useMemo(
    () => capabilities.filter((item) => item.canViewList || item.canCreate),
    [capabilities],
  );
  const quickSelectionItems = useMemo(() => {
    const topicsByKey = new Map(visibleTopics.map((topic) => [topic.key, topic]));
    return getRueckmeldungenMenuItems(user).map((menuItem) => {
      if (menuItem.path === '/rueckmeldung/selbstreflexion') {
        return {
          ...menuItem,
          key: 'self-reflection',
          kind: 'self-reflection',
          description: 'Eigenen Leistungsstand einschätzen und mit der Einschätzung der Lehrhöhenretter vergleichen.',
        };
      }
      if (menuItem.path === '/rueckmeldung/testbetrieb-vollduplexfunk') {
        return {
          ...menuItem,
          key: 'full-duplex-feedback',
          kind: 'route',
          description: 'Funkgerät, Kombinationsgerät und drei Headsets nach dem Schulnotenprinzip bewerten.',
        };
      }
      const topic = topicsByKey.get(MENU_PATH_TOPIC_MAP[menuItem.path]);
      return topic ? { ...topic, path: menuItem.path, kind: 'feedback' } : null;
    }).filter(Boolean);
  }, [user, visibleTopics]);

  const hasEvaluationPanel = Boolean(activeTopic?.canViewList);
  const formMeta = useMemo(
    () => getFormMeta(activeTopic?.key, Boolean(activeTopic?.canEvaluate)),
    [activeTopic?.key, activeTopic?.canEvaluate],
  );
  const useFullWidthIncidentPanels = Boolean(activeTopic?.key === 'zwischenfaelle' && hasEvaluationPanel);
  const isDispositionEvaluationView = Boolean(activeTopic?.key === 'disposition_hoerett' && activeTopic?.canEvaluate);
  const isWindenrettungEvaluationView = Boolean(activeTopic?.key === 'ars' && activeTopic?.canEvaluate);
  const isTechniquesEvaluationView = Boolean(activeTopic?.key === 'techniques_working_methods' && activeTopic?.canEvaluate);
  const canCollapseForm = Boolean(activeTopic?.canCreate && activeTopic?.canEvaluate);
  const showCollapsedForm = canCollapseForm && formCollapsed && !editingEntryId;
  const availableEntryYears = useMemo(() => getAvailableFeedbackYears(entries), [entries]);

  const canManageDetailEntry = useMemo(() => {
    if (!activeTopic || !detailEntry) {
      return false;
    }

    return activeTopic.canEvaluate || Number(detailEntry.created_by_user_id) === Number(user?.id);
  }, [activeTopic, detailEntry, user?.id]);

  const dispositionTeaserStats = useMemo(() => getDispositionTeaserStats(entries), [entries]);
  const windenrettungTeaserStats = useMemo(() => getWindenrettungTeaserStats(entries), [entries]);
  const filteredIncidentEntries = useMemo(
    () => filterIncidentEntries(entries, incidentYearFilters, incidentInjuryFilters),
    [entries, incidentInjuryFilters, incidentYearFilters],
  );
  const filteredDispositionEntries = useMemo(
    () => filterFeedbackEntriesByYears(entries, dispositionYearFilters).filter((entry) => {
      const matchesUrgency = !dispositionUrgencyFilters.length || dispositionUrgencyFilters.includes(entry.payload?.urgency);
      return matchesUrgency;
    }),
    [dispositionUrgencyFilters, dispositionYearFilters, entries],
  );
  const filteredWindenrettungEntries = useMemo(
    () => filterFeedbackEntriesByYears(entries, arsYearFilters).filter((entry) => {
      const matchesLocation = !arsLocationFilters.length || arsLocationFilters.includes(entry.payload?.locationType);
      const matchesWinchType = !arsWinchTypeFilters.length || arsWinchTypeFilters.includes(entry.payload?.winchType);
      const matchesColor = !arsColorFilters.length || arsColorFilters.includes(String(entry.display_color || '').trim());
      return matchesLocation && matchesWinchType && matchesColor;
    }),
    [entries, arsColorFilters, arsLocationFilters, arsWinchTypeFilters, arsYearFilters],
  );
  const filteredAppSupportEntries = useMemo(
    () => entries.filter((entry) => !appSupportColorFilters.length || appSupportColorFilters.includes(String(entry.display_color || '').trim())),
    [appSupportColorFilters, entries],
  );
  const filteredTechniquesEntries = useMemo(
    () => entries.filter((entry) => !techniquesColorFilters.length || techniquesColorFilters.includes(String(entry.display_color || '').trim())),
    [entries, techniquesColorFilters],
  );
  const appSupportColorStats = useMemo(() => getDisplayColorStats(entries), [entries]);
  const techniquesColorStats = useMemo(() => getDisplayColorStats(entries), [entries]);
  const statusStats = useMemo(
    () => {
      if (activeTopic?.key === 'app_support') {
        return getAppSupportStatusStats(entries);
      }
      if (activeTopic?.key === 'techniques_working_methods') {
        return getTechniquesStatusStats(entries);
      }
      return getStatusStats(entries);
    },
    [activeTopic?.key, entries],
  );
  const statusFilteredEntries = useMemo(
    () => {
      if (statusFilter === 'all') {
        return entries;
      }

      if (activeTopic?.key === 'app_support') {
        return entries.filter((entry) => matchesAppSupportStatusFilter(entry, statusFilter));
      }

      if (activeTopic?.key === 'techniques_working_methods') {
        return entries.filter((entry) => matchesTechniquesStatusFilter(entry, statusFilter));
      }

      return entries.filter((entry) => entry.status === statusFilter);
    },
    [activeTopic?.key, entries, statusFilter],
  );
  const visibleEntries = useMemo(
    () => {
      const baseEntries = activeTopic?.key === 'zwischenfaelle' && activeTopic.canEvaluate
        ? filteredIncidentEntries
        : (activeTopic?.key === 'disposition_hoerett' && activeTopic.canEvaluate
          ? filteredDispositionEntries
          : (activeTopic?.key === 'ars' && activeTopic.canEvaluate
            ? filteredWindenrettungEntries
            : (activeTopic?.key === 'app_support' && activeTopic.canEvaluate
              ? filteredAppSupportEntries
              : (activeTopic?.key === 'techniques_working_methods' && activeTopic.canEvaluate
                ? filteredTechniquesEntries
                : statusFilteredEntries))));

      if ((activeTopic?.key === 'ars' || activeTopic?.key === 'disposition_hoerett') && activeTopic.canEvaluate) {
        return baseEntries;
      }

      if (statusFilter === 'all') {
        return baseEntries;
      }

      if (activeTopic?.key === 'app_support') {
        return baseEntries.filter((entry) => matchesAppSupportStatusFilter(entry, statusFilter));
      }

      if (activeTopic?.key === 'techniques_working_methods') {
        return baseEntries.filter((entry) => matchesTechniquesStatusFilter(entry, statusFilter));
      }

      return baseEntries.filter((entry) => entry.status === statusFilter);
    },
    [activeTopic, filteredAppSupportEntries, filteredDispositionEntries, filteredIncidentEntries, filteredTechniquesEntries, filteredWindenrettungEntries, statusFilter, statusFilteredEntries],
  );

  useEffect(() => {
    async function loadInitialData() {
      try {
        setLoadingCapabilities(true);
        setError('');
        const [capabilitiesResponse, optionsResponse] = await Promise.all([
          getFeedbackCapabilities(),
          getFeedbackOptions(),
        ]);
        setCapabilities(capabilitiesResponse.data?.topics || []);
        setOptions({
          entryTypes: optionsResponse.data?.entryTypes || [],
          responsibles: optionsResponse.data?.responsibles || [],
          techniquesContacts: optionsResponse.data?.techniquesContacts || [],
        });
      } catch (loadError) {
        setError(loadError.response?.data?.error || 'Rückmeldungsdaten konnten nicht geladen werden.');
      } finally {
        setLoadingCapabilities(false);
      }
    }

    loadInitialData();
  }, []);

  useEffect(() => {
    setFormCollapsed(readInitialFormCollapsed(activeTopicKey));
    setFormState(getInitialFormState(activeTopicKey));
    setEditingEntryId(null);
    setAttachmentFiles([]);
    setResolutionPdfFiles([]);
    setSubmitDialog({ open: false, title: '', message: '' });
    setReviewDialog({ open: false, entry: null });
    setCompletionDialog({ open: false, entry: null });
    setDeleteDialog({ open: false, entry: null });
    setResolutionState(buildInitialResolutionState());
    setReopenDialog({ open: false, entry: null, nextStatus: 'open', reopenReason: '' });
    setIncidentYearFilters([]);
    setIncidentInjuryFilters([]);
    setStatusFilter('all');
    setDispositionYearFilters([]);
    setDispositionUrgencyFilters([]);
    setArsYearFilters([]);
    setArsLocationFilters([]);
    setArsWinchTypeFilters([]);
    setArsColorFilters([]);
    setAppSupportColorFilters([]);
    setTechniquesColorFilters([]);
    setSuccessMessage('');
    setAppSupportResponseDialog({ open: false, entry: null, responseMessage: '' });
  }, [activeTopicKey]);

  useEffect(() => {
    if (activeTopicKey !== 'techniques_working_methods' || editingEntryId || formState.contactUserId) {
      return;
    }

    const currentUserIsEligible = options.techniquesContacts.some((contact) => Number(contact.id) === Number(user?.id));
    if (currentUserIsEligible) {
      setFormState((prev) => ({ ...prev, contactUserId: String(user.id) }));
    }
  }, [activeTopicKey, editingEntryId, formState.contactUserId, options.techniquesContacts, user?.id]);

  useEffect(() => {
    if (!activeTopic || !activeTopic.canViewList) {
      setEntries([]);
      return;
    }

    async function loadEntries() {
      try {
        setLoadingEntries(true);
        setError('');
        const response = await listFeedbackEntries(activeTopic.key, sortKey);
        setEntries(response.data?.entries || []);
      } catch (loadError) {
        setError(loadError.response?.data?.error || 'Rückmeldungen konnten nicht geladen werden.');
      } finally {
        setLoadingEntries(false);
      }
    }

    loadEntries();
  }, [activeTopic, sortKey]);

  useEffect(() => {
    if (!activeTopic || !requestedEntryId || (activeTopic.key === 'techniques_working_methods' && !activeTopic.canEvaluate)) {
      return;
    }

    const existingEntry = entries.find((entry) => Number(entry.id) === Number(requestedEntryId));
    if (existingEntry) {
      setDetailEntry(existingEntry);
      return;
    }

    let isCancelled = false;

    async function loadRequestedEntry() {
      try {
        const response = await getFeedbackEntry(requestedEntryId);
        const entry = response.data?.entry;

        if (!entry || entry.topic_key !== activeTopic.key || isCancelled) {
          return;
        }

        setDetailEntry(entry);
      } catch (loadError) {
        if (isCancelled) {
          return;
        }

        if (![403, 404].includes(loadError.response?.status)) {
          setError(loadError.response?.data?.error || 'Rückmeldung konnte nicht geöffnet werden.');
        }
      }
    }

    loadRequestedEntry();

    return () => {
      isCancelled = true;
    };
  }, [activeTopic, entries, requestedEntryId]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(getFormCollapsedStorageKey(activeTopicKey), formCollapsed ? 'true' : 'false');
    } catch {
      // Ignore storage access issues and keep the in-memory state.
    }
  }, [activeTopicKey, formCollapsed]);

  const reloadEntries = async (preserveDetailId = null) => {
    if (!activeTopic?.canViewList) {
      return [];
    }

    const response = await listFeedbackEntries(activeTopic.key, sortKey);
    const updatedEntries = response.data?.entries || [];
    setEntries(updatedEntries);
    if (preserveDetailId) {
      setDetailEntry(updatedEntries.find((entry) => Number(entry.id) === Number(preserveDetailId)) || null);
    }
    return updatedEntries;
  };

  const updateRouteParams = (topicKey, entryId = null) => {
    const nextParams = { type: topicKey };
    if (entryId) {
      nextParams.entry = String(entryId);
    }
    setSearchParams(nextParams);
  };

  const handleTopicSelect = (topicKey) => {
    updateRouteParams(topicKey);
  };

  const handleCloseSubmitDialog = () => {
    setSubmitDialog({ open: false, title: '', message: '', showInjuryWarning: false });
  };

  const handleToggleFormCollapse = () => {
    if (!canCollapseForm || editingEntryId) {
      return;
    }

    setFormCollapsed((prev) => !prev);
  };

  const toggleArsFilterValue = (setter, value) => {
    setter((prev) => toggleExclusiveFilterValue(prev, value));
  };

  const toggleDispositionUrgencyFilterValue = (value) => {
    setDispositionUrgencyFilters((prev) => toggleMultiFilterValue(prev, value));
  };

  const openDetailEntry = (entry) => {
    if (!entry) {
      return;
    }

    setDetailEntry(entry);
    updateRouteParams(entry.topic_key || activeTopicKey, entry.id);
  };

  const closeDetailEntry = () => {
    Object.values(assetPreviewUrls).forEach((url) => URL.revokeObjectURL(url));
    setAssetPreviewUrls({});
    setDetailEntry(null);
    updateRouteParams(activeTopicKey);
  };

  const handleFormFieldChange = (fieldName, value) => {
    setFormState((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleStartEdit = (entry) => {
    setEditingEntryId(entry.id);
    setFormState(buildEditFormState(entry));
    setAttachmentFiles([]);
    setSuccessMessage('');
    setError('');
  };

  const handleCancelEdit = () => {
    setEditingEntryId(null);
    setFormState(getInitialFormState(activeTopicKey));
    setAttachmentFiles([]);
  };

  const openReviewDialog = (entry) => {
    setReviewDialog({ open: true, entry });
    setResolutionState(buildInitialResolutionState(entry));
    setResolutionPdfFiles([]);
  };

  const closeReviewDialog = () => {
    setReviewDialog({ open: false, entry: null });
    setResolutionState(buildInitialResolutionState());
    setResolutionPdfFiles([]);
  };

  const openCompletionDialog = (entry) => {
    setCompletionDialog({ open: true, entry });
    setResolutionState(buildInitialResolutionState(entry));
  };

  const closeCompletionDialog = () => {
    setCompletionDialog({ open: false, entry: null });
    setResolutionState(buildInitialResolutionState());
  };

  const openAppSupportResponseDialog = (entry) => {
    if (!entry || !activeTopic?.canEvaluate || entry.topic_key !== 'app_support' || entry.is_locked) {
      return;
    }

    setAppSupportResponseDialog({
      open: true,
      entry,
      responseMessage: entry.resolution?.responseMessage || '',
    });
  };

  const closeAppSupportResponseDialog = () => {
    setAppSupportResponseDialog({ open: false, entry: null, responseMessage: '' });
  };

  const openDeleteDialog = (entry) => {
    if (!entry || !activeTopic?.canEvaluate) {
      return;
    }

    setDeleteDialog({ open: true, entry });
  };

  const closeDeleteDialog = () => {
    if (deletingEntryId) {
      return;
    }

    setDeleteDialog({ open: false, entry: null });
  };

  const openReopenDialog = (entry, nextStatus) => {
    setReopenDialog({ open: true, entry, nextStatus, reopenReason: '' });
  };

  const closeReopenDialog = () => {
    setReopenDialog({ open: false, entry: null, nextStatus: 'open', reopenReason: '' });
  };

  const handleSubmit = async () => {
    if (!activeTopic || !activeTopic.canCreate) {
      return;
    }

    try {
      setSaving(true);
      setUploadingAssets(false);
      setError('');
      setSuccessMessage('');
      const payload = buildSubmitPayload(activeTopic.key, formState, options);
      const response = editingEntryId
        ? await updateFeedbackEntry(editingEntryId, payload)
        : await createFeedbackEntry(payload);
      const entryId = editingEntryId || response.data?.id;
      const files = [...attachmentFiles];

      if (entryId && files.length > 0) {
        setUploadingAssets(true);
        await uploadFeedbackAssets(entryId, files);
      }

      if (activeTopic.canViewList) {
        await reloadEntries();
      }

      setSuccessMessage(editingEntryId ? 'Rückmeldung aktualisiert.' : 'Rückmeldung gespeichert.');
      setSubmitDialog({
        open: true,
        title: editingEntryId ? 'Rückmeldung aktualisiert' : 'Rückmeldung vollständig übermittelt',
        message: files.length
          ? `${activeTopic.label}: Alle Daten inklusive ${files.length} Anhang${files.length === 1 ? '' : 'e'} wurden erfolgreich übertragen.`
          : `${activeTopic.label}: Alle Daten wurden erfolgreich übertragen.`,
        showInjuryWarning:
          activeTopic.key === 'zwischenfaelle' && payload.payload?.injuryType === 'with_person_damage',
      });
      handleCancelEdit();
    } catch (submitError) {
      setError(submitError.response?.data?.error || 'Rückmeldung konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
      setUploadingAssets(false);
    }
  };

  const handleDeleteAsset = async (asset) => {
    if (!detailEntry || !asset) {
      return;
    }

    const confirmed = window.confirm(`Anhang "${asset.original_name}" wirklich entfernen?`);
    if (!confirmed) {
      return;
    }

    try {
      setDeletingAssetId(asset.id);
      setError('');
      setSuccessMessage('');
      await deleteFeedbackAsset(detailEntry.id, asset.id);
      await reloadEntries(detailEntry.id);
      setSuccessMessage('Anhang entfernt.');
    } catch (deleteError) {
      setError(deleteError.response?.data?.error || 'Anhang konnte nicht entfernt werden.');
    } finally {
      setDeletingAssetId(null);
    }
  };

  const handleConfirmDeleteEntry = async () => {
    const entry = deleteDialog.entry;
    if (!entry || !activeTopic?.canEvaluate) {
      return;
    }

    try {
      setDeletingEntryId(entry.id);
      setError('');
      setSuccessMessage('');
      await deleteFeedbackEntry(entry.id);
      await reloadEntries();
      if (Number(detailEntry?.id) === Number(entry.id)) {
        closeDetailEntry();
      }
      setDeleteDialog({ open: false, entry: null });
      setSuccessMessage('Rückmeldung gelöscht.');
    } catch (deleteError) {
      setError(deleteError.response?.data?.error || 'Rückmeldung konnte nicht gelöscht werden.');
    } finally {
      setDeletingEntryId(null);
    }
  };

  const handleConfirmAppSupportResponse = async () => {
    const entry = appSupportResponseDialog.entry;
    if (!entry) {
      return;
    }

    try {
      setUpdatingStatusId(entry.id);
      setError('');
      setSuccessMessage('');
      await updateFeedbackStatus(entry.id, {
        status: 'resolved',
        resolution: {
          responseMessage: appSupportResponseDialog.responseMessage,
        },
      });
      await reloadEntries(detailEntry && Number(detailEntry.id) === Number(entry.id) ? entry.id : null);
      setSuccessMessage('App-Support-Rückmeldung beantwortet und abgeschlossen.');
      closeAppSupportResponseDialog();
    } catch (statusError) {
      setError(statusError.response?.data?.error || 'App-Support-Rückmeldung konnte nicht abgeschlossen werden.');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleUpdateDisplayColor = async (entry, displayColor) => {
    if (!entry?.id) {
      return;
    }

    try {
      setUpdatingColorId(entry.id);
      setError('');
      setSuccessMessage('');
      await updateFeedbackDisplayColor(entry.id, displayColor);
      await reloadEntries(detailEntry && Number(detailEntry.id) === Number(entry.id) ? entry.id : null);
      setSuccessMessage('Eintragsfarbe aktualisiert.');
    } catch (updateError) {
      setError(updateError.response?.data?.error || 'Eintragsfarbe konnte nicht aktualisiert werden.');
    } finally {
      setUpdatingColorId(null);
    }
  };

  const handleExportWindenrettungPdf = async () => {
    if (!isWindenrettungEvaluationView || !visibleEntries.length) {
      return;
    }

    try {
      setExportingWindenrettungPdf(true);
      setError('');
      setSuccessMessage('');

      const brandingAssets = await loadLogbuchPdfBrandingAssets();
      const doc = buildWindenrettungPdfDocument({
        entries: visibleEntries,
        brandingAssets,
        exportDate: new Date(),
        filters: {
          yearLabels: arsYearFilters.map((value) => formatYearLabel(Number(value))),
          locationLabels: arsLocationFilters.map((value) => formatWindenrettungLocation(value)),
          winchTypeLabels: arsWinchTypeFilters.map((value) => formatWindenrettungWinchType(value)),
          colorLabels: arsColorFilters.map((value) => formatWindenrettungColor(value)),
        },
      });

      pdfMake.createPdf(doc).download(buildWindenrettungPdfFilename(new Date()));
      setSuccessMessage('Windenrettung-PDF erstellt.');
    } catch (exportError) {
      setError(exportError.message || 'Windenrettung-PDF konnte nicht erstellt werden.');
    } finally {
      setExportingWindenrettungPdf(false);
    }
  };

  const handleExportDispositionPdf = async () => {
    if (!isDispositionEvaluationView || !visibleEntries.length) {
      return;
    }

    try {
      setExportingDispositionPdf(true);
      setError('');
      setSuccessMessage('');

      const brandingAssets = await loadLogbuchPdfBrandingAssets();
      const doc = buildDispositionPdfDocument({
        entries: visibleEntries,
        brandingAssets,
        exportDate: new Date(),
        filters: {
          yearLabels: dispositionYearFilters.map((value) => formatYearLabel(Number(value))),
          urgencyLabels: dispositionUrgencyFilters.map((value) => formatDispositionUrgency(value)),
        },
      });

      pdfMake.createPdf(doc).download(buildDispositionPdfFilename(new Date()));
      setSuccessMessage('Disposition-HöRett-PDF erstellt.');
    } catch (exportError) {
      setError(exportError.message || 'Disposition-HöRett-PDF konnte nicht erstellt werden.');
    } finally {
      setExportingDispositionPdf(false);
    }
  };

  const handleStatusChange = async (entry, status) => {
    if (!activeTopic?.canEvaluate) {
      return;
    }

    if (!entry) {
      return;
    }

    if (entry.status === 'resolved' && status !== 'resolved') {
      openReopenDialog(entry, status);
      return;
    }

    if (entry.topic_key === 'zwischenfaelle' && status === 'in_review') {
      openReviewDialog(entry);
      return;
    }

    if (entry.topic_key === 'zwischenfaelle' && status === 'resolved') {
      if (entry.status !== 'in_review' || !isResolutionStateValid(buildInitialResolutionState(entry))) {
        setError('Bitte die Aufarbeitung zuerst im Status "In Prüfung" vollständig erfassen.');
        return;
      }
      openCompletionDialog(entry);
      return;
    }

    try {
      setUpdatingStatusId(entry.id);
      setError('');
      setSuccessMessage('');
      await updateFeedbackStatus(entry.id, { status });
      await reloadEntries(detailEntry && Number(detailEntry.id) === Number(entry.id) ? entry.id : null);
      setSuccessMessage('Status aktualisiert.');
    } catch (statusError) {
      setError(statusError.response?.data?.error || 'Status konnte nicht aktualisiert werden.');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleStartIncidentReview = async (entry) => {
    if (!entry || !activeTopic?.canEvaluate) {
      return;
    }

    if (entry.status === 'in_review') {
      openReviewDialog(entry);
      return;
    }

    try {
      setUpdatingStatusId(entry.id);
      setError('');
      setSuccessMessage('');
      await updateFeedbackStatus(entry.id, { status: 'in_review' });
      const updatedEntries = await reloadEntries(detailEntry && Number(detailEntry.id) === Number(entry.id) ? entry.id : null);
      const updatedEntry = updatedEntries.find((item) => Number(item.id) === Number(entry.id)) || { ...entry, status: 'in_review' };
      openReviewDialog(updatedEntry);
      setSuccessMessage('Zwischenfall ist jetzt in Prüfung.');
    } catch (statusError) {
      setError(statusError.response?.data?.error || 'Zwischenfall konnte nicht in Prüfung gesetzt werden.');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleConfirmCompletion = async () => {
    const entry = completionDialog.entry;
    if (!entry) {
      return;
    }

    try {
      setUpdatingStatusId(entry.id);
      setError('');
      setSuccessMessage('');

      await updateFeedbackStatus(entry.id, {
        status: 'resolved',
        publishToGroup: resolutionState.publishToGroup,
      });

      await reloadEntries(detailEntry && Number(detailEntry.id) === Number(entry.id) ? entry.id : null);
      setSuccessMessage(
        resolutionState.publishToGroup
          ? 'Zwischenfall abgeschlossen und Mitteilung versendet.'
          : 'Zwischenfall abgeschlossen.',
      );
      closeCompletionDialog();
    } catch (statusError) {
      setError(statusError.response?.data?.error || 'Zwischenfall konnte nicht abgeschlossen werden.');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleConfirmReview = async () => {
    const entry = reviewDialog.entry;
    if (!entry) {
      return;
    }

    try {
      setUpdatingStatusId(entry.id);
      setError('');
      setSuccessMessage('');

      let followUpPdfAssetId = entry.resolution?.followUpPdfAssetId || null;
      if (resolutionPdfFiles.length > 0) {
        const uploadResponse = await uploadFeedbackAssets(entry.id, resolutionPdfFiles.slice(0, 1));
        followUpPdfAssetId = uploadResponse.data?.assets?.[0]?.id || null;
      }

      await updateFeedbackStatus(entry.id, {
        status: 'in_review',
        resolution: {
          needsFollowUp: resolutionState.needsFollowUp,
          noFollowUpReason: resolutionState.needsFollowUp === 'no' ? resolutionState.noFollowUpReason : '',
          followUpSummary: resolutionState.needsFollowUp === 'yes' ? resolutionState.followUpSummary : '',
          followUpPdfAssetId: resolutionState.needsFollowUp === 'yes' ? followUpPdfAssetId : null,
        },
      });

      await reloadEntries(detailEntry && Number(detailEntry.id) === Number(entry.id) ? entry.id : null);
      setSuccessMessage('Aufarbeitung gespeichert und Status auf In Prüfung gesetzt.');
      closeReviewDialog();
    } catch (statusError) {
      setError(statusError.response?.data?.error || 'Aufarbeitung konnte nicht gespeichert werden.');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleConfirmReopen = async () => {
    const entry = reopenDialog.entry;
    if (!entry) {
      return;
    }

    try {
      setUpdatingStatusId(entry.id);
      setError('');
      setSuccessMessage('');
      await updateFeedbackStatus(entry.id, {
        status: reopenDialog.nextStatus,
        reopenReason: reopenDialog.reopenReason,
      });
      await reloadEntries(detailEntry && Number(detailEntry.id) === Number(entry.id) ? entry.id : null);
      setSuccessMessage('Zwischenfall wurde wieder zur Bearbeitung geöffnet.');
      closeReopenDialog();
    } catch (statusError) {
      setError(statusError.response?.data?.error || 'Zwischenfall konnte nicht wieder geöffnet werden.');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handlePreviewAsset = async (entryId, asset) => {
    if (!asset || !entryId) {
      return;
    }

    if (asset.asset_kind !== 'document' && assetPreviewUrls[asset.id]) {
      return;
    }

    try {
      setLoadingAssetId(asset.id);
      const response = await getFeedbackAssetBlob(entryId, asset.id, false);
      const objectUrl = URL.createObjectURL(response.data);

      if (asset.asset_kind === 'document') {
        window.open(objectUrl, '_blank', 'noopener,noreferrer');
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      } else {
        setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: objectUrl }));
      }
    } catch (assetError) {
      setError(assetError.response?.data?.error || 'Anhang konnte nicht geladen werden.');
    } finally {
      setLoadingAssetId(null);
    }
  };

  const handleDownloadAsset = async (entryId, asset) => {
    if (!asset || !entryId) {
      return;
    }

    try {
      setLoadingAssetId(asset.id);
      const response = await getFeedbackAssetBlob(entryId, asset.id, true);
      const objectUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = asset.original_name || 'download';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (assetError) {
      setError(assetError.response?.data?.error || 'Anhang konnte nicht heruntergeladen werden.');
    } finally {
      setLoadingAssetId(null);
    }
  };

  const renderFeedbackFormFields = () => {
    if (!activeTopic) {
      return null;
    }

    if (activeTopic.key === 'zwischenfaelle') {
      return (
        <IncidentFeedbackFormFields
          formState={formState}
          onFieldChange={handleFormFieldChange}
          options={options}
          descriptionLabel={formMeta.descriptionLabel}
          linksLabel={formMeta.linksLabel}
          attachmentFiles={attachmentFiles}
          onAttachmentFilesChange={setAttachmentFiles}
          showAssetPickers
        />
      );
    }

    if (activeTopic.key === 'techniques_working_methods') {
      return (
        <>
          <TextField
            label="Überschrift"
            value={formState.title}
            onChange={(event) => handleFormFieldChange('title', event.target.value)}
            fullWidth
            required
          />

          <TextField
            label={formMeta.descriptionLabel}
            value={formState.description}
            onChange={(event) => handleFormFieldChange('description', event.target.value)}
            fullWidth
            multiline
            minRows={5}
            required
          />

          <TextField
            select
            label="Für Rückfragen"
            value={formState.contactUserId || ''}
            onChange={(event) => handleFormFieldChange('contactUserId', event.target.value)}
            fullWidth
            required
          >
            {(options.techniquesContacts || []).map((contact) => (
              <MenuItem key={contact.id} value={String(contact.id)}>
                {contact.full_name}
              </MenuItem>
            ))}
          </TextField>

          <FeedbackFileDropzone
            label="Anhänge (Bilder, Videos & Dateien)"
            accept="image/*,video/*,audio/*,text/*,application/*"
            files={attachmentFiles}
            onFilesChange={setAttachmentFiles}
            buttonLabel="Anhänge auswählen"
          />
        </>
      );
    }

    if (activeTopic.key === 'app_support') {
      return (
        <>
          <Stack spacing={0.75}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Art des Anliegens
            </Typography>
            <Grid container spacing={1}>
              {APP_SUPPORT_REPORT_KIND_OPTIONS.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.value}>
                  <ToggleButton
                    value={item.value}
                    selected={formState.reportKind === item.value}
                    onClick={() => handleFormFieldChange('reportKind', item.value)}
                    sx={{
                      width: '100%',
                      borderRadius: 1.5,
                      justifyContent: 'center',
                      px: 1.5,
                      py: 1,
                      textTransform: 'none',
                      borderColor: formState.reportKind === item.value ? 'primary.main' : 'divider',
                    }}
                  >
                    {item.label}
                  </ToggleButton>
                </Grid>
              ))}
            </Grid>
          </Stack>

          <Stack spacing={0.75}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Betroffener Bereich der App
            </Typography>
            <Grid container spacing={1}>
              {APP_SUPPORT_AREA_OPTIONS.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.value}>
                  <ToggleButton
                    value={item.value}
                    selected={formState.appArea === item.value}
                    onClick={() => handleFormFieldChange('appArea', item.value)}
                    sx={{
                      width: '100%',
                      borderRadius: 1.5,
                      justifyContent: 'center',
                      px: 1.5,
                      py: 1,
                      textTransform: 'none',
                      borderColor: formState.appArea === item.value ? 'primary.main' : 'divider',
                    }}
                  >
                    {item.label}
                  </ToggleButton>
                </Grid>
              ))}
            </Grid>
          </Stack>

          <TextField
            label={formMeta.descriptionLabel}
            value={formState.description}
            onChange={(event) => handleFormFieldChange('description', event.target.value)}
            fullWidth
            multiline
            minRows={4}
          />

          <TextField
            label={formMeta.linksLabel}
            value={formState.linksText}
            onChange={(event) => handleFormFieldChange('linksText', event.target.value)}
            fullWidth
            multiline
            minRows={3}
          />

          <FeedbackFileDropzone
            label="Anhänge"
            accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
            files={attachmentFiles}
            onFilesChange={setAttachmentFiles}
            buttonLabel="Anhänge auswählen"
          />
        </>
      );
    }

    return (
      <>
        <DateInputField
          label={activeTopic.key === 'disposition_hoerett' ? 'Datum des Einsatzes' : 'Datum'}
          value={formState.eventDate}
          onChange={(value) => handleFormFieldChange('eventDate', value)}
          helperText="Z. B. 14042026 oder 14.04.2026"
          fullWidth
        />

        <TextField
          label={activeTopic.key === 'disposition_hoerett' ? 'Einsatzstichwort' : 'Titel'}
          value={formState.title}
          onChange={(event) => handleFormFieldChange('title', event.target.value)}
          fullWidth
        />

        {activeTopic.key === 'disposition_hoerett' && renderDispositionFields()}

        {activeTopic.key === 'ars' && (
          <>
            <Stack spacing={0.75}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Örtlichkeit
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                value={formState.locationType}
                onChange={(_, value) => {
                  if (value) {
                    handleFormFieldChange('locationType', value);
                  }
                }}
              >
                <ToggleButton value="nrw">NRW</ToggleButton>
                <ToggleButton value="other">Andere Örtlichkeit</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <Stack spacing={0.75}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Art der Windenrettung
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                value={formState.winchType}
                onChange={(_, value) => {
                  if (value) {
                    handleFormFieldChange('winchType', value);
                  }
                }}
              >
                <ToggleButton value="winch_operation">Windeneinsatz</ToggleButton>
                <ToggleButton value="winch_option">Windenoption</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <TextField
              select
              label="Vermuteter Zeitvorteil durch Windenrettung"
              value={String(formState.estimatedTimeAdvantageMinutes)}
              onChange={(event) => handleFormFieldChange('estimatedTimeAdvantageMinutes', Number(event.target.value))}
              fullWidth
            >
              {TIME_ADVANTAGE_MINUTE_OPTIONS.map((minutes) => (
                <MenuItem key={minutes} value={String(minutes)}>
                  {minutes} Minuten
                </MenuItem>
              ))}
            </TextField>
          </>
        )}

        <TextField
          label={formMeta.descriptionLabel}
          value={formState.description}
          onChange={(event) => handleFormFieldChange('description', event.target.value)}
          fullWidth
          multiline
          minRows={4}
        />

        <TextField
          label={formMeta.linksLabel}
          value={formState.linksText}
          onChange={(event) => handleFormFieldChange('linksText', event.target.value)}
          fullWidth
          multiline
          minRows={3}
        />

        <FeedbackFileDropzone
          label="Anhänge (Bilder, Videos & sonstiges)"
          accept="image/*,video/*,audio/*,text/*,application/*"
          files={attachmentFiles}
          onFilesChange={setAttachmentFiles}
          buttonLabel="Anhänge auswählen"
        />
      </>
    );
  };

  const renderDispositionFields = () => (
    <Stack spacing={1.25}>
      <ToggleButtonGroup
        exclusive
        value={formState.urgency}
        onChange={(_, value) => {
          if (value) {
            handleFormFieldChange('urgency', value);
          }
        }}
        fullWidth
      >
        {DISPOSITION_URGENCY_OPTIONS.map((item) => (
          <ToggleButton key={item.value} value={item.value}>{item.label}</ToggleButton>
        ))}
      </ToggleButtonGroup>

      <TextField
        label="Einsatzleiter (optional)"
        value={formState.deploymentReference}
        onChange={(event) => handleFormFieldChange('deploymentReference', event.target.value)}
        fullWidth
      />

      <TextField
        label="Beschreibung der Einsatzsituation und Einsatzoption der Höhenrettung"
        value={formState.recommendation}
        onChange={(event) => handleFormFieldChange('recommendation', event.target.value)}
        fullWidth
        multiline
        minRows={3}
      />

      <TextField
        select
        label="Vermuteter Zeitvorteil durch HöRett bei direkter Alarmierung"
        value={String(formState.estimatedTimeAdvantageMinutes)}
        onChange={(event) => handleFormFieldChange('estimatedTimeAdvantageMinutes', Number(event.target.value))}
        fullWidth
      >
        {TIME_ADVANTAGE_MINUTE_OPTIONS.map((minutes) => (
          <MenuItem key={minutes} value={String(minutes)}>
            {minutes} Minuten
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );

  const renderStatusSelect = (entry, compact = false) => {
    if (activeTopic?.key === 'ars') {
      return null;
    }

    if (activeTopic?.key === 'app_support') {
      return (
        <Chip
          size={compact ? 'small' : 'medium'}
          color={formatAppSupportStatusColor(entry.status)}
          label={formatAppSupportStatusLabel(entry.status)}
        />
      );
    }

    if (activeTopic?.key === 'techniques_working_methods') {
      const statusOption = formatTechniquesStatus(entry.status);
      if (!activeTopic.canEvaluate) {
        return (
          <Chip
            size={compact ? 'small' : 'medium'}
            color={statusOption.color}
            label={statusOption.label}
          />
        );
      }

      return (
        <TextField
          select
          size="small"
          label={compact ? undefined : 'Status'}
          value={statusOption.value}
          onChange={(event) => handleStatusChange(entry, event.target.value)}
          disabled={updatingStatusId === entry.id}
          sx={{ minWidth: compact ? 190 : 230 }}
        >
          {TECHNIQUES_STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </TextField>
      );
    }

    if (!activeTopic?.canEvaluate) {
      return (
        <Chip
          size={compact ? 'small' : 'medium'}
          color={formatStatusColor(entry.status)}
          label={formatStatusLabel(entry.status)}
        />
      );
    }

    return (
      <TextField
        select
        size="small"
        label={compact ? undefined : 'Status'}
        value={entry.status || 'open'}
        onChange={(event) => handleStatusChange(entry, event.target.value)}
        disabled={updatingStatusId === entry.id}
        sx={{ minWidth: compact ? 140 : 180 }}
      >
        {STATUS_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
        ))}
      </TextField>
    );
  };

  const renderEntryMeta = (entry) => {
    const canSeeIncidentPeople = activeTopic?.canEvaluate || Number(entry.created_by_user_id) === Number(user?.id);

    if (entry.topic_key === 'zwischenfaelle') {
      return (
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Art der Tätigkeit</Typography>
            <Typography variant="body2">{entry.payload?.entryType || '-'}</Typography>
          </Grid>
          {canSeeIncidentPeople && (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Verantwortlicher</Typography>
              <Typography variant="body2">{entry.payload?.responsibleNameSnapshot || '-'}</Typography>
            </Grid>
          )}
          {canSeeIncidentPeople && (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Meldender</Typography>
              <Typography variant="body2">{getIncidentReporterLabel(entry)}</Typography>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Letzte Änderung</Typography>
            <Typography variant="body2">{formatDateTime(entry.updated_at)}</Typography>
          </Grid>
        </Grid>
      );
    }

    if (entry.topic_key === 'disposition_hoerett') {
      return (
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Alarmierungsbewertung</Typography>
            <Typography variant="body2">{formatDispositionUrgency(entry.payload?.urgency)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Einsatzleiter</Typography>
            <Typography variant="body2">{entry.payload?.deploymentReference || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Zeitvorteil</Typography>
            <Typography variant="body2">{Number(entry.payload?.estimatedTimeAdvantageMinutes) || 0} Minuten</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Letzte Änderung</Typography>
            <Typography variant="body2">{formatDateTime(entry.updated_at)}</Typography>
          </Grid>
        </Grid>
      );
    }

    if (entry.topic_key === 'ars') {
      return (
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Örtlichkeit</Typography>
            <Typography variant="body2">{formatWindenrettungLocation(entry.payload?.locationType)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Art der Windenrettung</Typography>
            <Typography variant="body2">{formatWindenrettungWinchType(entry.payload?.winchType)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Zeitvorteil</Typography>
            <Typography variant="body2">{Number(entry.payload?.estimatedTimeAdvantageMinutes) || 0} Minuten</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Letzte Änderung</Typography>
            <Typography variant="body2">{formatDateTime(entry.updated_at)}</Typography>
          </Grid>
        </Grid>
      );
    }

    if (entry.topic_key === 'app_support') {
      return (
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Art des Anliegens</Typography>
            <Typography variant="body2">{formatAppSupportReportKind(entry.payload?.reportKind)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Betroffener Bereich der App</Typography>
            <Typography variant="body2">{formatAppSupportArea(entry.payload?.appArea)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Typography variant="body2">{formatAppSupportStatusLabel(entry.status)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Letzte Änderung</Typography>
            <Typography variant="body2">{formatDateTime(entry.updated_at)}</Typography>
          </Grid>
        </Grid>
      );
    }

    return (
      <Typography variant="body2" color="text.secondary">
        Letzte Änderung: {formatDateTime(entry.updated_at)}
      </Typography>
    );
  };

  return (
    <Box sx={{ px: { xs: 1, sm: 3 }, py: 2 }}>
      <Stack spacing={2.5}>
        <Card sx={{ borderRadius: 3, border: 1, borderColor: 'divider', background: 'linear-gradient(135deg, rgba(255,255,255,0.98), rgba(248,250,252,0.96))' }}>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Rückmeldeformulare
              </Typography>

              <Grid container spacing={2}>
                {quickSelectionItems.map((item) => (
                  <Grid item xs={12} md={4} key={item.key}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        borderColor: item.kind === 'feedback' && activeTopicKey === item.key ? 'primary.main' : 'divider',
                        bgcolor: item.kind === 'feedback' && activeTopicKey === item.key ? 'action.selected' : 'background.paper',
                        cursor: 'pointer',
                        transition: 'transform 160ms ease, box-shadow 160ms ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 3,
                        },
                      }}
                      onClick={() => item.kind !== 'feedback'
                        ? navigate(item.path)
                        : handleTopicSelect(item.key)}
                    >
                      <CardContent>
                        <Stack spacing={1}>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {item.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.description}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </CardContent>
        </Card>

        {loadingCapabilities && <LinearProgress />}
        {error && <Alert severity="error">{error}</Alert>}
        {successMessage && <Alert severity="success">{successMessage}</Alert>}

        <Card sx={{ borderRadius: 3, border: 1, borderColor: 'divider' }}>
          <CardContent>
            {!activeTopic && (
              <Alert severity="info">
                Für den aktuellen Benutzer ist kein Rückmeldungsthema freigeschaltet.
              </Alert>
            )}

            {activeTopic && (
              <Grid container spacing={2.5} justifyContent={hasEvaluationPanel ? 'flex-start' : 'center'}>
                <Grid item xs={12} lg={useFullWidthIncidentPanels ? 12 : hasEvaluationPanel ? 5 : 9} xl={useFullWidthIncidentPanels ? 12 : hasEvaluationPanel ? 4.5 : 8}>
                  <Card variant="outlined" sx={{ width: '100%', borderRadius: 2.5, height: '100%', boxShadow: hasEvaluationPanel ? 'none' : 2 }}>
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {formMeta.heading}
                            </Typography>
                          </Box>
                          {canCollapseForm && !editingEntryId && (
                            <Tooltip title={showCollapsedForm ? 'Maske einblenden' : 'Maske verkleinern'}>
                              <IconButton
                                size="small"
                                onClick={handleToggleFormCollapse}
                                sx={{
                                  alignSelf: { xs: 'flex-start', sm: 'center' },
                                  border: 1,
                                  borderColor: 'divider',
                                  borderRadius: 1.5,
                                }}
                              >
                                <KeyboardArrowDownIcon
                                  sx={{
                                    transform: showCollapsedForm ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 160ms ease',
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>

                        {!activeTopic.canCreate && (
                          <Alert severity="info">
                            Für dieses Thema hast du aktuell keine Erfassungsberechtigung.
                          </Alert>
                        )}

                        {activeTopic.canCreate && (
                          <Stack spacing={1.25}>
                            {editingEntryId && (
                              <Alert severity="info">
                                Die Bearbeitung der Stammdaten ist in einem separaten Fenster geöffnet.
                              </Alert>
                            )}

                            {!hasEvaluationPanel && (
                              <Alert severity="info">
                                Für dieses Thema wird bewusst nur die Eingabemaske angezeigt. Es gibt daher keine rechte Auflistungsfläche.
                              </Alert>
                            )}

                            <Collapse in={!showCollapsedForm && !editingEntryId} unmountOnExit>
                              <Stack spacing={1.25}>
                                {renderFeedbackFormFields()}

                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                  <Button variant="contained" onClick={handleSubmit} disabled={saving || uploadingAssets}>
                                    {saving || uploadingAssets
                                      ? (uploadingAssets ? 'Lädt Anhänge hoch...' : 'Speichert...')
                                      : 'Speichern'}
                                  </Button>
                                </Stack>
                              </Stack>
                            </Collapse>
                          </Stack>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                {hasEvaluationPanel && (
                  <Grid item xs={12} lg={useFullWidthIncidentPanels ? 12 : 7} xl={useFullWidthIncidentPanels ? 12 : 7.5}>
                    <Card variant="outlined" sx={{ width: '100%', borderRadius: 2.5, height: '100%' }}>
                      <CardContent>
                        <Stack spacing={1.5}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {activeTopic.key === 'techniques_working_methods' && !activeTopic.canEvaluate ? 'Übersicht' : 'Auswertung'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {activeTopic.key === 'techniques_working_methods' && !activeTopic.canEvaluate
                                  ? 'Überschrift, Eingabedatum und Bearbeitungsstatus der eingegangenen Meldungen.'
                                  : 'Status, Sichtung und Bearbeitung vorhandener Rückmeldungen.'}
                              </Typography>
                            </Box>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                              {!isDispositionEvaluationView && !isWindenrettungEvaluationView && (
                                <TextField
                                  select
                                  size="small"
                                  label="Sortierung"
                                  value={sortKey}
                                  onChange={(event) => setSortKey(event.target.value)}
                                  sx={{ minWidth: { xs: '100%', sm: 220 } }}
                                >
                                  {(activeTopic.sortOptions || []).map((sortOption) => (
                                    <MenuItem key={sortOption.key} value={sortOption.key}>{sortOption.label}</MenuItem>
                                  ))}
                                </TextField>
                              )}
                              {isDispositionEvaluationView && (
                                <Button
                                  variant="outlined"
                                  onClick={handleExportDispositionPdf}
                                  disabled={exportingDispositionPdf || !visibleEntries.length}
                                  sx={{ whiteSpace: 'nowrap' }}
                                >
                                  {exportingDispositionPdf ? 'Erstellt PDF...' : 'PDF-Export'}
                                </Button>
                              )}
                              {isWindenrettungEvaluationView && (
                                <Button
                                  variant="outlined"
                                  onClick={handleExportWindenrettungPdf}
                                  disabled={exportingWindenrettungPdf || !visibleEntries.length}
                                  sx={{ whiteSpace: 'nowrap' }}
                                >
                                  {exportingWindenrettungPdf ? 'Erstellt PDF...' : 'PDF-Export'}
                                </Button>
                              )}
                            </Stack>
                          </Stack>

                          {!isWindenrettungEvaluationView && !isDispositionEvaluationView && !isTechniquesEvaluationView && (
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              {activeTopic?.key === 'app_support' ? (
                                <>
                                  <Chip size="small" color="warning" label={`Offen: ${statusStats.open}`} />
                                  <Chip size="small" color="success" label={`Erledigt: ${statusStats.resolved}`} />
                                </>
                              ) : (
                                <>
                                  <Chip size="small" color="warning" label={`Neu: ${statusStats.open}`} />
                                  <Chip size="small" color="info" label={`In Prüfung: ${statusStats.in_review}`} />
                                  <Chip size="small" color="success" label={`Abgeschlossen: ${statusStats.resolved}`} />
                                </>
                              )}
                            </Stack>
                          )}

                          {isTechniquesEvaluationView && (
                            <Stack spacing={1.5}>
                              <Card variant="outlined" sx={{ borderRadius: 2.5, bgcolor: 'grey.50' }}>
                                <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                                  <Stack spacing={1.5}>
                                    <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.2 }}>
                                      Filteransicht
                                    </Typography>
                                    <Grid container spacing={1.5}>
                                      <Grid item xs={12} md={6}>
                                        <Stack spacing={0.75}>
                                          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.2 }}>
                                            Status
                                          </Typography>
                                          <FormControl fullWidth size="small">
                                            <InputLabel id="techniques-status-filter-label">Statusfilter</InputLabel>
                                            <Select
                                              labelId="techniques-status-filter-label"
                                              value={statusFilter}
                                              label="Statusfilter"
                                              onChange={(event) => setStatusFilter(event.target.value)}
                                            >
                                              <MenuItem value="all">Alle Status</MenuItem>
                                              {TECHNIQUES_STATUS_OPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                        </Stack>
                                      </Grid>
                                      <Grid item xs={12} md={6}>
                                        <Stack spacing={0.75}>
                                          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.2 }}>
                                            Farben
                                          </Typography>
                                          <FormControl fullWidth size="small">
                                            <InputLabel id="techniques-color-filter-label">Farben</InputLabel>
                                            <Select
                                              multiple
                                              labelId="techniques-color-filter-label"
                                              value={techniquesColorFilters}
                                              onChange={(event) => setTechniquesColorFilters(normalizeMultiSelectInput(event.target.value))}
                                              input={<OutlinedInput label="Farben" />}
                                              renderValue={() => techniquesColorFilters.length
                                                ? techniquesColorFilters.map((value) => formatWindenrettungColor(value)).join(', ')
                                                : 'Alle Farben'}
                                            >
                                              {WINDENRETTUNG_COLOR_OPTIONS.map((option) => (
                                                <MenuItem key={option.value || 'none'} value={option.value}>
                                                  <Checkbox checked={techniquesColorFilters.includes(option.value)} />
                                                  <Stack direction="row" spacing={1} alignItems="center">
                                                    <Box
                                                      sx={{
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        bgcolor: option.color || 'background.paper',
                                                        border: 1,
                                                        borderColor: 'divider',
                                                      }}
                                                    />
                                                    <ListItemText primary={`${option.label} (${techniquesColorStats[String(option.value || '').trim()] || 0})`} />
                                                  </Stack>
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                        </Stack>
                                      </Grid>
                                    </Grid>
                                  </Stack>
                                </CardContent>
                              </Card>

                              <Stack spacing={1.5}>
                                <Divider />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                  Filterkacheln
                                </Typography>
                                <Grid container spacing={1.25}>
                                  {TECHNIQUES_STATUS_OPTIONS.map((option) => {
                                    const selected = statusFilter === option.value;
                                    return (
                                      <Grid item xs={12} sm={6} md={4} key={`techniques-status-${option.value}`}>
                                        <Card
                                          variant="outlined"
                                          onClick={() => setStatusFilter(selected ? 'all' : option.value)}
                                          sx={{
                                            borderRadius: 2.5,
                                            cursor: 'pointer',
                                            borderColor: selected ? 'primary.main' : 'divider',
                                            bgcolor: selected ? 'action.selected' : 'background.paper',
                                          }}
                                        >
                                          <CardContent>
                                            <Stack spacing={0.75}>
                                              <Typography variant="body2" color="text.secondary">
                                                {option.label}
                                              </Typography>
                                              <Typography variant="h5" sx={{ fontWeight: 700, color: `${option.color}.main` }}>
                                                {statusStats[option.value] || 0}
                                              </Typography>
                                              <Typography variant="caption" color="text.secondary">
                                                {selected ? 'Aktiv' : 'Zum Filtern anklicken'}
                                              </Typography>
                                            </Stack>
                                          </CardContent>
                                        </Card>
                                      </Grid>
                                    );
                                  })}
                                </Grid>
                              </Stack>
                            </Stack>
                          )}

                          {isDispositionEvaluationView && (
                            <Stack spacing={1.5}>
                              <Card variant="outlined" sx={{ borderRadius: 2.5, bgcolor: 'grey.50' }}>
                                <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                                  <Stack spacing={1.5}>
                                    <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.2 }}>
                                      Filteransicht
                                    </Typography>
                                    <Grid container spacing={1.5}>
                                      <Grid item xs={12}>
                                        <Stack spacing={0.75}>
                                          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.2 }}>
                                            Jahre
                                          </Typography>
                                          <FormControl fullWidth size="small">
                                            <InputLabel id="disposition-year-filter-label">Jahre</InputLabel>
                                            <Select
                                              multiple
                                              labelId="disposition-year-filter-label"
                                              value={dispositionYearFilters}
                                              onChange={(event) => {
                                                setDispositionYearFilters(normalizeMultiSelectInput(event.target.value));
                                              }}
                                              input={<OutlinedInput label="Jahre" />}
                                              renderValue={() => dispositionYearFilters.length
                                                ? dispositionYearFilters.join(', ')
                                                : 'Alle Jahre'}
                                            >
                                              {availableEntryYears.map((year) => (
                                                <MenuItem key={year} value={String(year)}>
                                                  <Checkbox checked={dispositionYearFilters.includes(String(year))} />
                                                  <ListItemText primary={formatYearLabel(year)} />
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                        </Stack>
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Stack spacing={0.75}>
                                          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.2 }}>
                                            Alarmierungsbewertung
                                          </Typography>
                                          <FormControl fullWidth size="small">
                                            <InputLabel id="disposition-urgency-filter-label">Alarmierungsbewertung</InputLabel>
                                            <Select
                                              multiple
                                              labelId="disposition-urgency-filter-label"
                                              value={dispositionUrgencyFilters}
                                              onChange={(event) => {
                                                setDispositionUrgencyFilters(normalizeMultiSelectInput(event.target.value));
                                              }}
                                              input={<OutlinedInput label="Alarmierungsbewertung" />}
                                              renderValue={() => dispositionUrgencyFilters.length
                                                ? dispositionUrgencyFilters.map((value) => formatDispositionUrgency(value)).join(', ')
                                                : 'Alle Bewertungen'}
                                            >
                                              {DISPOSITION_URGENCY_OPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                  <Checkbox checked={dispositionUrgencyFilters.includes(option.value)} />
                                                  <ListItemText primary={option.label} />
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                        </Stack>
                                      </Grid>
                                    </Grid>
                                  </Stack>
                                </CardContent>
                              </Card>

                              <Stack spacing={1.5}>
                                <Divider />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                  Filterkacheln
                                </Typography>
                                <Grid container spacing={1.25}>
                                  {[
                                    { key: 'niedrig', label: 'HöRett nicht alarmiert', count: dispositionTeaserStats.niedrig, tone: 'warning.main', selected: dispositionUrgencyFilters.includes('niedrig') },
                                    { key: 'mittel', label: 'HöRett verzögert alarmiert', count: dispositionTeaserStats.mittel, tone: 'info.main', selected: dispositionUrgencyFilters.includes('mittel') },
                                    { key: 'hoch', label: 'Alarmierung HöRett verweigert', count: dispositionTeaserStats.hoch, tone: 'error.main', selected: dispositionUrgencyFilters.includes('hoch') },
                                  ].map((item) => (
                                    <Grid item xs={12} md={4} key={item.key}>
                                      <Card
                                        variant="outlined"
                                        sx={{
                                          borderRadius: 2.5,
                                          cursor: 'pointer',
                                          borderColor: item.selected ? 'primary.main' : 'divider',
                                          bgcolor: item.selected ? 'action.selected' : 'background.paper',
                                        }}
                                        onClick={() => toggleDispositionUrgencyFilterValue(item.key)}
                                      >
                                        <CardContent>
                                          <Stack spacing={0.75}>
                                            <Typography variant="body2" color="text.secondary">
                                              {item.label}
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 700, color: item.tone }}>
                                              {item.count}
                                            </Typography>
                                          </Stack>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Stack>
                            </Stack>
                          )}

                          {isWindenrettungEvaluationView && (
                            <Stack spacing={1.5}>
                              <Card variant="outlined" sx={{ borderRadius: 2.5, bgcolor: 'grey.50' }}>
                                <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                                  <Stack spacing={1.5}>
                                    <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.2 }}>
                                      Filteransicht
                                    </Typography>
                                    <Grid container spacing={1.5}>
                                      <Grid item xs={12} md={4}>
                                        <Stack spacing={0.75}>
                                          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.2 }}>
                                            Jahre
                                          </Typography>
                                          <FormControl fullWidth size="small">
                                            <InputLabel id="windenrettung-year-filter-label">Jahre</InputLabel>
                                            <Select
                                              multiple
                                              labelId="windenrettung-year-filter-label"
                                              value={arsYearFilters}
                                              onChange={(event) => {
                                                setArsYearFilters(normalizeMultiSelectInput(event.target.value));
                                              }}
                                              input={<OutlinedInput label="Jahre" />}
                                              renderValue={() => arsYearFilters.length
                                                ? arsYearFilters.join(', ')
                                                : 'Alle Jahre'}
                                            >
                                              {availableEntryYears.map((year) => (
                                                <MenuItem key={year} value={String(year)}>
                                                  <Checkbox checked={arsYearFilters.includes(String(year))} />
                                                  <ListItemText primary={formatYearLabel(year)} />
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                        </Stack>
                                      </Grid>
                                      <Grid item xs={12} md={4}>
                                        <Stack spacing={0.75}>
                                          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.2 }}>
                                            Örtlichkeit
                                          </Typography>
                                          <FormControl fullWidth size="small">
                                            <InputLabel id="windenrettung-location-filter-label">Örtlichkeit</InputLabel>
                                            <Select
                                              multiple
                                              labelId="windenrettung-location-filter-label"
                                              value={arsLocationFilters}
                                              onChange={(event) => {
                                                setArsLocationFilters(normalizeExclusiveFilterInput(event.target.value));
                                              }}
                                              input={<OutlinedInput label="Örtlichkeit" />}
                                              renderValue={() => arsLocationFilters.length
                                                ? arsLocationFilters.map((value) => formatWindenrettungLocation(value)).join(', ')
                                                : 'Alle Örtlichkeiten'}
                                            >
                                              {WINDENRETTUNG_LOCATION_OPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                  <Checkbox checked={arsLocationFilters.includes(option.value)} />
                                                  <ListItemText primary={option.label} />
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                        </Stack>
                                      </Grid>
                                      <Grid item xs={12} md={4}>
                                        <Stack spacing={0.75}>
                                          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.2 }}>
                                            Art der Windenrettung
                                          </Typography>
                                          <FormControl fullWidth size="small">
                                            <InputLabel id="windenrettung-winch-filter-label">Art der Windenrettung</InputLabel>
                                            <Select
                                              multiple
                                              labelId="windenrettung-winch-filter-label"
                                              value={arsWinchTypeFilters}
                                              onChange={(event) => {
                                                setArsWinchTypeFilters(normalizeExclusiveFilterInput(event.target.value));
                                              }}
                                              input={<OutlinedInput label="Art der Windenrettung" />}
                                              renderValue={() => arsWinchTypeFilters.length
                                                ? arsWinchTypeFilters.map((value) => formatWindenrettungWinchType(value)).join(', ')
                                                : 'Alle Arten'}
                                            >
                                              {WINDENRETTUNG_WINCH_TYPE_OPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                  <Checkbox checked={arsWinchTypeFilters.includes(option.value)} />
                                                  <ListItemText primary={option.label} />
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                        </Stack>
                                      </Grid>
                                      <Grid item xs={12} md={4}>
                                        <Stack spacing={0.75}>
                                          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.2 }}>
                                            Farben
                                          </Typography>
                                          <FormControl fullWidth size="small">
                                            <InputLabel id="windenrettung-color-filter-label">Farben</InputLabel>
                                            <Select
                                              multiple
                                              labelId="windenrettung-color-filter-label"
                                              value={arsColorFilters}
                                              onChange={(event) => {
                                                setArsColorFilters(normalizeMultiSelectInput(event.target.value));
                                              }}
                                              input={<OutlinedInput label="Farben" />}
                                              renderValue={() => arsColorFilters.length
                                                ? arsColorFilters.map((value) => formatWindenrettungColor(value)).join(', ')
                                                : 'Alle Farben'}
                                            >
                                              {WINDENRETTUNG_COLOR_OPTIONS.filter((option) => option.value).map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                  <Checkbox checked={arsColorFilters.includes(option.value)} />
                                                  <Stack direction="row" spacing={1} alignItems="center">
                                                    <Box
                                                      sx={{
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        bgcolor: option.color,
                                                        border: 1,
                                                        borderColor: 'divider',
                                                      }}
                                                    />
                                                    <ListItemText primary={option.label} />
                                                  </Stack>
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                        </Stack>
                                      </Grid>
                                    </Grid>
                                  </Stack>
                                </CardContent>
                              </Card>

                              <Stack spacing={1.5}>
                                <Divider />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                  Filterkacheln
                                </Typography>
                                <Grid container spacing={1.25}>
                                  {[
                                    { key: 'nrw', label: 'NRW', count: windenrettungTeaserStats.nrw, tone: 'success.main', selected: arsLocationFilters.includes('nrw'), onClick: () => toggleArsFilterValue(setArsLocationFilters, 'nrw') },
                                    { key: 'other', label: 'Andere Örtlichkeit', count: windenrettungTeaserStats.other, tone: 'warning.main', selected: arsLocationFilters.includes('other'), onClick: () => toggleArsFilterValue(setArsLocationFilters, 'other') },
                                    { key: 'winch_operation', label: 'Windeneinsatz', count: windenrettungTeaserStats.winch_operation, tone: 'primary.main', selected: arsWinchTypeFilters.includes('winch_operation'), onClick: () => toggleArsFilterValue(setArsWinchTypeFilters, 'winch_operation') },
                                    { key: 'winch_option', label: 'Windenoption', count: windenrettungTeaserStats.winch_option, tone: 'info.main', selected: arsWinchTypeFilters.includes('winch_option'), onClick: () => toggleArsFilterValue(setArsWinchTypeFilters, 'winch_option') },
                                  ].map((item) => (
                                    <Grid item xs={12} sm={6} md={3} key={item.key}>
                                      <Card
                                        variant="outlined"
                                        sx={{
                                          borderRadius: 2.5,
                                          cursor: 'pointer',
                                          borderColor: item.selected ? 'primary.main' : 'divider',
                                          bgcolor: item.selected ? 'action.selected' : 'background.paper',
                                        }}
                                        onClick={item.onClick}
                                      >
                                        <CardContent>
                                          <Stack spacing={0.75}>
                                            <Typography variant="body2" color="text.secondary">
                                              {item.label}
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 700, color: item.tone }}>
                                              {item.count}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              {item.selected ? 'Aktiv' : 'Zum Filtern anklicken'}
                                            </Typography>
                                          </Stack>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Stack>
                            </Stack>
                          )}

                          {activeTopic.key === 'zwischenfaelle' && activeTopic.canEvaluate && (
                            <Stack spacing={1.5}>
                              <Card variant="outlined" sx={{ borderRadius: 2.5, bgcolor: 'grey.50' }}>
                                <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                                  <Stack spacing={1.5}>
                                    <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.2 }}>
                                      Filteransicht
                                    </Typography>
                                    <Grid container spacing={1.5}>
                                      <Grid item xs={12} md={6}>
                                        <Stack spacing={0.75}>
                                          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.2 }}>
                                            Jahre
                                          </Typography>
                                          <FormControl fullWidth size="small">
                                            <InputLabel id="incident-year-filter-label">Jahre</InputLabel>
                                            <Select
                                              multiple
                                              labelId="incident-year-filter-label"
                                              value={incidentYearFilters}
                                              onChange={(event) => {
                                                setIncidentYearFilters(normalizeMultiSelectInput(event.target.value));
                                              }}
                                              input={<OutlinedInput label="Jahre" />}
                                              renderValue={() => incidentYearFilters.length
                                                ? incidentYearFilters.join(', ')
                                                : 'Alle Jahre'}
                                            >
                                              {availableEntryYears.map((year) => (
                                                <MenuItem key={year} value={String(year)}>
                                                  <Checkbox checked={incidentYearFilters.includes(String(year))} />
                                                  <ListItemText primary={formatYearLabel(year)} />
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                        </Stack>
                                      </Grid>
                                      <Grid item xs={12} md={6}>
                                        <Stack spacing={0.75}>
                                          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.2 }}>
                                            Status
                                          </Typography>
                                          <FormControl fullWidth size="small">
                                            <InputLabel id="incident-status-filter-label">Statusfilter</InputLabel>
                                            <Select
                                              labelId="incident-status-filter-label"
                                              value={statusFilter}
                                              label="Statusfilter"
                                              onChange={(event) => setStatusFilter(event.target.value)}
                                            >
                                              <MenuItem value="all">Alle Status</MenuItem>
                                              {STATUS_OPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                        </Stack>
                                      </Grid>
                                      <Grid item xs={12} md={6}>
                                        <Stack spacing={0.75}>
                                          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.2 }}>
                                            Personenschaden
                                          </Typography>
                                          <FormControl fullWidth size="small">
                                            <InputLabel id="incident-injury-filter-label">Personenschaden</InputLabel>
                                            <Select
                                              multiple
                                              labelId="incident-injury-filter-label"
                                              value={incidentInjuryFilters}
                                              onChange={(event) => {
                                                setIncidentInjuryFilters(normalizeMultiSelectInput(event.target.value));
                                              }}
                                              input={<OutlinedInput label="Personenschaden" />}
                                              renderValue={() => incidentInjuryFilters.length
                                                ? incidentInjuryFilters.map((value) => formatInjuryType(value)).join(', ')
                                                : 'Mit und ohne Personenschaden'}
                                            >
                                              {['with_person_damage', 'without_person_damage'].map((value) => (
                                                <MenuItem key={value} value={value}>
                                                  <Checkbox checked={incidentInjuryFilters.includes(value)} />
                                                  <ListItemText primary={formatInjuryType(value)} />
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                        </Stack>
                                      </Grid>
                                    </Grid>
                                  </Stack>
                                </CardContent>
                              </Card>
                            </Stack>
                          )}

                          {activeTopic.key === 'app_support' && activeTopic.canEvaluate && (
                            <Stack spacing={1.5}>
                              <Card variant="outlined" sx={{ borderRadius: 2.5, bgcolor: 'grey.50' }}>
                                <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                                  <Stack spacing={1.5}>
                                    <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.2 }}>
                                      Filteransicht
                                    </Typography>
                                    <Grid container spacing={1.5}>
                                      <Grid item xs={12} md={4}>
                                        <Stack spacing={0.75}>
                                          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.2 }}>
                                            Status
                                          </Typography>
                                          <FormControl fullWidth size="small">
                                            <InputLabel id="app-support-status-filter-label">Statusfilter</InputLabel>
                                            <Select
                                              labelId="app-support-status-filter-label"
                                              value={statusFilter}
                                              label="Statusfilter"
                                              onChange={(event) => setStatusFilter(event.target.value)}
                                            >
                                              <MenuItem value="all">Alle Status</MenuItem>
                                              {APP_SUPPORT_STATUS_OPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                        </Stack>
                                      </Grid>
                                    </Grid>
                                  </Stack>
                                </CardContent>
                              </Card>

                              <Stack spacing={1.5}>
                                <Divider />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                  Farbverteilung
                                </Typography>
                                <Grid container spacing={1.25}>
                                  {WINDENRETTUNG_COLOR_OPTIONS.map((option) => {
                                    const colorKey = String(option.value || '').trim();
                                    const selected = appSupportColorFilters.includes(colorKey);
                                    const count = appSupportColorStats[colorKey] || 0;
                                    return (
                                      <Grid item xs={12} sm={6} md={4} lg={3} key={`app-support-filter-${option.value || 'none'}`}>
                                        <Card
                                          variant="outlined"
                                          sx={{
                                            borderRadius: 2.5,
                                            cursor: 'pointer',
                                            borderColor: selected ? 'primary.main' : 'divider',
                                            bgcolor: selected ? 'action.selected' : 'background.paper',
                                          }}
                                          onClick={() => setAppSupportColorFilters((prev) => toggleMultiFilterValue(prev, colorKey))}
                                        >
                                          <CardContent>
                                            <Stack spacing={0.75}>
                                              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                                <Typography variant="body2" color="text.secondary">
                                                  {option.label}
                                                </Typography>
                                                <Box
                                                  sx={{
                                                    width: 14,
                                                    height: 14,
                                                    borderRadius: '50%',
                                                    bgcolor: option.value || 'background.paper',
                                                    border: 1,
                                                    borderColor: option.value || 'divider',
                                                  }}
                                                />
                                              </Stack>
                                              <Typography
                                                variant="h5"
                                                sx={{
                                                  fontWeight: 700,
                                                  color: option.value ? option.color : 'text.secondary',
                                                }}
                                              >
                                                {count}
                                              </Typography>
                                              <Typography variant="caption" color="text.secondary">
                                                {selected ? 'Aktiv' : 'Zum Filtern anklicken'}
                                              </Typography>
                                            </Stack>
                                          </CardContent>
                                        </Card>
                                      </Grid>
                                    );
                                  })}
                                </Grid>
                              </Stack>
                            </Stack>
                          )}

                          {loadingEntries && <LinearProgress />}

                          {!loadingEntries && entries.length === 0 && (
                            <Alert severity="info">Noch keine Rückmeldungen für dieses Thema vorhanden.</Alert>
                          )}

                          {!loadingEntries && entries.length > 0 && visibleEntries.length === 0 && (
                            <Alert severity="info">
                              {(isWindenrettungEvaluationView || isDispositionEvaluationView || isTechniquesEvaluationView || (activeTopic.key === 'zwischenfaelle' && activeTopic.canEvaluate) || (activeTopic.key === 'app_support' && activeTopic.canEvaluate))
                                ? 'Für die gewählten Filter sind aktuell keine Rückmeldungen vorhanden.'
                                : 'Für den gewählten Statusfilter sind aktuell keine Rückmeldungen vorhanden.'}
                            </Alert>
                          )}

                          <Stack spacing={1.25}>
                            {visibleEntries.map((entry) => {
                              const imageCount = formatAssetCount(entry.assets, 'image');
                              const videoCount = formatAssetCount(entry.assets, 'video');
                              const documentCount = formatAssetCount(entry.assets, 'document');

                              if (activeTopic.key === 'techniques_working_methods' && !activeTopic.canEvaluate) {
                                return (
                                  <Card key={entry.id} variant="outlined" sx={{ borderRadius: 2.5 }}>
                                    <CardContent>
                                      <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        justifyContent="space-between"
                                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                                        spacing={1}
                                      >
                                        <Box>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            {entry.title}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            Eingabedatum: {formatDate(entry.event_date)}
                                          </Typography>
                                        </Box>
                                        {renderStatusSelect(entry, true)}
                                      </Stack>
                                    </CardContent>
                                  </Card>
                                );
                              }

                              return (
                                <Card
                                  key={entry.id}
                                  variant="outlined"
                                  sx={{
                                    borderRadius: 2.5,
                                    borderLeft: (entry.topic_key === 'ars' || entry.topic_key === 'app_support' || entry.topic_key === 'techniques_working_methods') && entry.display_color ? `8px solid ${entry.display_color}` : undefined,
                                  }}
                                >
                                  <CardContent>
                                    <Stack spacing={1.25}>
                                      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.25}>
                                        <Box>
                                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                              {entry.title}
                                            </Typography>
                                            {entry.topic_key === 'techniques_working_methods' && (
                                              <Chip
                                                size="small"
                                                color={formatTechniquesStatus(entry.status).color}
                                                label={formatTechniquesStatus(entry.status).label}
                                              />
                                            )}
                                          </Stack>
                                          <Typography variant="body2" color="text.secondary">
                                            {formatDate(entry.event_date)}
                                          </Typography>
                                        </Box>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                          {(activeTopic.canEvaluate || Number(entry.created_by_user_id) === Number(user?.id)) && !entry.is_locked && (
                                            <Tooltip title="Die Ursprungsmeldung kann hierüber angepasst werden, wenn zum Zwischenfall noch weitere Erkenntnisse gesammelt wurden.">
                                              <Button size="small" variant="text" onClick={() => handleStartEdit(entry)}>
                                                Stammdaten der Meldung ergänzen
                                              </Button>
                                            </Tooltip>
                                          )}
                                          {(entry.topic_key === 'ars' || entry.topic_key === 'app_support' || entry.topic_key === 'techniques_working_methods') && activeTopic.canEvaluate && (
                                            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                              {WINDENRETTUNG_COLOR_OPTIONS.map((option) => (
                                                <Tooltip key={`${entry.id}-${option.value || 'none'}`} title={option.label}>
                                                  <IconButton
                                                    size="small"
                                                    disabled={updatingColorId === entry.id}
                                                    onClick={() => handleUpdateDisplayColor(entry, option.value)}
                                                    sx={{
                                                      width: 22,
                                                      height: 22,
                                                      border: 1,
                                                      borderColor: entry.display_color === option.value ? 'primary.main' : 'divider',
                                                      bgcolor: option.value || 'background.paper',
                                                    }}
                                                  >
                                                    {option.value === '' && (
                                                      <Box sx={{ width: 10, height: 1.5, bgcolor: 'text.secondary', borderRadius: 999 }} />
                                                    )}
                                                  </IconButton>
                                                </Tooltip>
                                              ))}
                                            </Stack>
                                          )}
                                          {renderStatusSelect(entry, true)}
                                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                            {entry.topic_key === 'zwischenfaelle' && (
                                              <Chip
                                                size="small"
                                                color={entry.payload?.injuryType === 'with_person_damage' ? 'error' : 'success'}
                                                label={formatInjuryType(entry.payload?.injuryType)}
                                              />
                                            )}
                                            {entry.topic_key === 'disposition_hoerett' && (
                                              <Chip size="small" variant="outlined" label={formatDispositionUrgency(entry.payload?.urgency)} />
                                            )}
                                            {entry.topic_key === 'app_support' && (
                                              <>
                                                <Chip size="small" variant="outlined" label={formatAppSupportReportKind(entry.payload?.reportKind)} />
                                                <Chip size="small" variant="outlined" label={formatAppSupportArea(entry.payload?.appArea)} />
                                              </>
                                            )}
                                            {imageCount > 0 && <Chip size="small" variant="outlined" label={formatAssetChipLabel(imageCount, 'Bild', 'Bilder')} />}
                                            {videoCount > 0 && <Chip size="small" variant="outlined" label={formatAssetChipLabel(videoCount, 'Video', 'Videos')} />}
                                            {documentCount > 0 && <Chip size="small" variant="outlined" label={formatAssetChipLabel(documentCount, 'Dokument', 'Dokumente')} />}
                                          </Stack>
                                          {activeTopic.canEvaluate && (
                                            <Tooltip title="Rückmeldung löschen">
                                              <span>
                                                <IconButton
                                                  size="small"
                                                  color="error"
                                                  onClick={() => openDeleteDialog(entry)}
                                                  disabled={deletingEntryId === entry.id}
                                                  sx={{ width: { xs: 42, sm: 34 }, height: { xs: 42, sm: 34 } }}
                                                >
                                                  <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                              </span>
                                            </Tooltip>
                                          )}
                                        </Stack>
                                      </Stack>

                                      {renderEntryMeta(entry)}

                                      <Typography variant="body2" color="text.secondary">
                                        {String(entry.description || '').slice(0, 220)}{String(entry.description || '').length > 220 ? '...' : ''}
                                      </Typography>

                                      {entry.topic_key === 'app_support' && entry.resolution?.responseMessage && (
                                        <Alert severity="success" sx={{ py: 0.5 }}>
                                          Antwort: {entry.resolution.responseMessage}
                                        </Alert>
                                      )}

                                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                        <Button size="small" variant="outlined" onClick={() => openDetailEntry(entry)}>
                                          Details
                                        </Button>
                                        {entry.topic_key === 'app_support' && activeTopic.canEvaluate && !entry.is_locked && (
                                          <Button size="small" variant="contained" onClick={() => openAppSupportResponseDialog(entry)}>
                                            Rückmeldung
                                          </Button>
                                        )}
                                        {entry.topic_key === 'zwischenfaelle' && activeTopic.canEvaluate && entry.status === 'open' && (
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            disabled={updatingStatusId === entry.id}
                                            onClick={() => handleStartIncidentReview(entry)}
                                          >
                                            {updatingStatusId === entry.id ? 'Lädt...' : 'Zwischenfall auswerten'}
                                          </Button>
                                        )}
                                        {entry.topic_key === 'zwischenfaelle' && activeTopic.canEvaluate && entry.status === 'in_review' && !entry.is_locked && (
                                          <Button size="small" variant="outlined" onClick={() => openReviewDialog(entry)}>
                                            Aufarbeitung bearbeiten
                                          </Button>
                                        )}
                                        {entry.topic_key === 'zwischenfaelle' && activeTopic.canEvaluate && entry.status === 'in_review' && entry.resolution?.needsFollowUp && !entry.is_locked && (
                                          <Button size="small" variant="contained" onClick={() => openCompletionDialog(entry)}>
                                            Aufarbeitung abschließen
                                          </Button>
                                        )}
                                      </Stack>
                                    </Stack>
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            )}
          </CardContent>
        </Card>
      </Stack>

      <Dialog open={Boolean(detailEntry)} onClose={closeDetailEntry} maxWidth="md" fullWidth>
        <DialogTitle>{detailEntry?.title || 'Rückmeldung'}</DialogTitle>
        <DialogContent dividers>
          {detailEntry && (
            <Stack spacing={1.5}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  Datum: {formatDate(detailEntry.event_date)}
                </Typography>
                {renderStatusSelect(detailEntry)}
              </Stack>

              <Typography variant="body2">
                {detailEntry.description}
              </Typography>

              {detailEntry.is_locked && (
                <Alert severity="info">
                  {detailEntry.topic_key === 'zwischenfaelle'
                    ? 'Dieser Zwischenfall ist abgeschlossen und schreibgeschützt. Änderungen sind erst nach einer Wiederöffnung mit Begründung möglich.'
                    : 'Diese Rückmeldung ist abgeschlossen und schreibgeschützt.'}
                </Alert>
              )}

              {detailEntry.topic_key === 'zwischenfaelle' && (
                <Stack spacing={0.5}>
                  <Typography variant="body2">Personenschaden: {formatInjuryType(detailEntry.payload?.injuryType)}</Typography>
                  <Typography variant="body2">Art der Tätigkeit: {detailEntry.payload?.entryType || '-'}</Typography>
                  {(activeTopic?.canEvaluate || Number(detailEntry.created_by_user_id) === Number(user?.id)) && (
                    <Typography variant="body2">Verantwortlicher: {detailEntry.payload?.responsibleNameSnapshot || '-'}</Typography>
                  )}
                  {(activeTopic?.canEvaluate || Number(detailEntry.created_by_user_id) === Number(user?.id)) && (
                    <Typography variant="body2">Meldender: {getIncidentReporterLabel(detailEntry)}</Typography>
                  )}
                </Stack>
              )}

              {detailEntry.topic_key === 'zwischenfaelle' && detailEntry.resolution?.needsFollowUp && (
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Auswertung</Typography>
                  <Typography variant="body2">
                    Aufarbeitung benötigt: {detailEntry.resolution.needsFollowUp === 'yes' ? 'Ja' : 'Nein'}
                  </Typography>
                  {detailEntry.resolution.needsFollowUp === 'no' && (
                    <Typography variant="body2">
                      Begründung: {detailEntry.resolution.noFollowUpReason || '-'}
                    </Typography>
                  )}
                  {detailEntry.resolution.needsFollowUp === 'yes' && (
                    <Typography variant="body2">
                      Inhalt der Aufarbeitung: {detailEntry.resolution.followUpSummary || '-'}
                    </Typography>
                  )}
                  {detailEntry.published_to_group && (
                    <Typography variant="body2">Mitteilung an berechtigte Benutzer wurde versendet.</Typography>
                  )}
                  {detailEntry.reopen_reason && (activeTopic?.canEvaluate || Number(detailEntry.created_by_user_id) === Number(user?.id)) && (
                    <Alert severity="warning" sx={{ mt: 0.5 }}>
                      Wieder geöffnet von {detailEntry.reopened_by_name || 'Unbekannt'}
                      {detailEntry.reopened_at ? ` am ${formatDateTime(detailEntry.reopened_at)}` : ''}: {detailEntry.reopen_reason}
                    </Alert>
                  )}
                  {activeTopic?.canEvaluate && detailEntry.status === 'in_review' && !detailEntry.is_locked && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ pt: 0.5 }}>
                      <Button size="small" variant="outlined" onClick={() => openReviewDialog(detailEntry)}>
                        Aufarbeitung bearbeiten
                      </Button>
                      {detailEntry.resolution?.needsFollowUp && (
                        <Button size="small" variant="contained" onClick={() => openCompletionDialog(detailEntry)}>
                          Aufarbeitung abschließen
                        </Button>
                      )}
                    </Stack>
                  )}
                </Stack>
              )}

              {detailEntry.topic_key === 'disposition_hoerett' && (
                <Stack spacing={0.5}>
                  <Typography variant="body2">Alarmierungsbewertung: {formatDispositionUrgency(detailEntry.payload?.urgency)}</Typography>
                  <Typography variant="body2">Einsatzleiter: {detailEntry.payload?.deploymentReference || '-'}</Typography>
                  <Typography variant="body2">Beschreibung der Einsatzsituation und Einsatzoption der Höhenrettung: {detailEntry.payload?.recommendation || '-'}</Typography>
                  <Typography variant="body2">Vermuteter Zeitvorteil durch HöRett bei direkter Alarmierung: {Number(detailEntry.payload?.estimatedTimeAdvantageMinutes) || 0} Minuten</Typography>
                </Stack>
              )}

              {detailEntry.topic_key === 'ars' && (
                <Stack spacing={0.5}>
                  <Typography variant="body2">Örtlichkeit: {formatWindenrettungLocation(detailEntry.payload?.locationType)}</Typography>
                  <Typography variant="body2">Art der Windenrettung: {formatWindenrettungWinchType(detailEntry.payload?.winchType)}</Typography>
                  <Typography variant="body2">Vermuteter Zeitvorteil durch Windenrettung: {Number(detailEntry.payload?.estimatedTimeAdvantageMinutes) || 0} Minuten</Typography>
                </Stack>
              )}

              {detailEntry.topic_key === 'app_support' && (
                <Stack spacing={0.75}>
                  <Typography variant="body2">Art des Anliegens: {formatAppSupportReportKind(detailEntry.payload?.reportKind)}</Typography>
                  <Typography variant="body2">Betroffener Bereich der App: {formatAppSupportArea(detailEntry.payload?.appArea)}</Typography>
                  {detailEntry.resolution?.responseMessage && (
                    <Alert severity="success">
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Rückmeldung</Typography>
                      <Typography variant="body2">{detailEntry.resolution.responseMessage}</Typography>
                    </Alert>
                  )}
                  {activeTopic?.canEvaluate && !detailEntry.is_locked && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ pt: 0.5 }}>
                      <Button size="small" variant="contained" onClick={() => openAppSupportResponseDialog(detailEntry)}>
                        Rückmeldung verfassen
                      </Button>
                    </Stack>
                  )}
                </Stack>
              )}

              {detailEntry.topic_key === 'techniques_working_methods' && (
                <Typography variant="body2">
                  Für Rückfragen: {detailEntry.payload?.contactNameSnapshot || '-'}
                </Typography>
              )}

              {Array.isArray(detailEntry.payload?.links) && detailEntry.payload.links.length > 0 && (
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>URLs</Typography>
                  {detailEntry.payload.links.map((link) => (
                    <Typography key={link} variant="body2">
                      <a href={link} target="_blank" rel="noreferrer">{link}</a>
                    </Typography>
                  ))}
                </Stack>
              )}

              {detailEntry.assets?.length > 0 && (
                <Stack spacing={1}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Anhänge</Typography>
                  {detailEntry.assets.map((asset) => (
                    <Box key={asset.id}>
                      {assetPreviewUrls[asset.id] && asset.asset_kind === 'image' && (
                        <Box component="img" src={assetPreviewUrls[asset.id]} alt={asset.original_name} sx={{ maxWidth: '100%', maxHeight: 260, borderRadius: 2, border: 1, borderColor: 'divider', mb: 1 }} />
                      )}
                      {assetPreviewUrls[asset.id] && asset.asset_kind === 'video' && (
                        <Box component="video" controls src={assetPreviewUrls[asset.id]} sx={{ width: '100%', maxHeight: 320, borderRadius: 2, mb: 1 }} />
                      )}
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                        <Stack spacing={0.25}>
                          <Typography variant="caption" color="text.secondary">
                            {asset.original_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {asset.asset_kind === 'document' ? 'Dokument' : asset.asset_kind === 'video' ? 'Video' : 'Bild'}
                          </Typography>
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={loadingAssetId === asset.id}
                            onClick={() => handlePreviewAsset(detailEntry.id, asset)}
                          >
                            {loadingAssetId === asset.id ? 'Lädt...' : (asset.asset_kind === 'document' ? 'Dokument öffnen' : 'Ansehen')}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={loadingAssetId === asset.id}
                            onClick={() => handleDownloadAsset(detailEntry.id, asset)}
                          >
                            Herunterladen
                          </Button>
                          {canManageDetailEntry && !detailEntry.is_locked && (
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              disabled={deletingAssetId === asset.id}
                              onClick={() => handleDeleteAsset(asset)}
                            >
                              {deletingAssetId === asset.id ? 'Entfernt...' : 'Anhang löschen'}
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}

              <Typography variant="caption" color="text.secondary">
                Angelegt von {detailEntry.created_by_name || '-'} am {formatDateTime(detailEntry.created_at)}. Letzte Änderung: {formatDateTime(detailEntry.updated_at)}
              </Typography>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialog.open} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Rückmeldung löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Soll die Rückmeldung &quot;{deleteDialog.entry?.title || 'dieser Eintrag'}&quot; wirklich gelöscht werden?
            Anhänge und Auswertungsdaten werden ebenfalls entfernt.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={Boolean(deletingEntryId)}>
            Abbrechen
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDeleteEntry}
            disabled={Boolean(deletingEntryId)}
          >
            {deletingEntryId ? 'Lösche...' : 'Löschen'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={submitDialog.open} onClose={handleCloseSubmitDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{submitDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {submitDialog.message}
          </DialogContentText>
          {submitDialog.showInjuryWarning && (
            <Alert severity="error" variant="filled" sx={{ mt: 2 }}>
              Informiere bitte unverzüglich die Leitung der Höhenrettung! Es ist zu einem Personenschaden gekommen.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleCloseSubmitDialog}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={appSupportResponseDialog.open} onClose={closeAppSupportResponseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>App-Support beantworten</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <DialogContentText>
              Diese Rückmeldung wird an den meldenden Nutzer gesendet und der Eintrag anschließend abgeschlossen.
            </DialogContentText>
            <Typography variant="body2" color="text.secondary">
              {appSupportResponseDialog.entry?.title || 'App-Support'}
            </Typography>
            <TextField
              label="Rückmeldung"
              value={appSupportResponseDialog.responseMessage}
              onChange={(event) => setAppSupportResponseDialog((prev) => ({ ...prev, responseMessage: event.target.value }))}
              fullWidth
              multiline
              minRows={4}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAppSupportResponseDialog} disabled={updatingStatusId === appSupportResponseDialog.entry?.id}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmAppSupportResponse}
            disabled={!String(appSupportResponseDialog.responseMessage || '').trim() || updatingStatusId === appSupportResponseDialog.entry?.id}
          >
            {updatingStatusId === appSupportResponseDialog.entry?.id ? 'Speichert...' : 'Antwort senden und abschließen'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editingEntryId)}
        onClose={() => {
          if (!saving && !uploadingAssets) {
            handleCancelEdit();
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Stammdaten der Meldung ergänzen</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <DialogContentText>
              Die bisherigen Meldedaten sind hier eingeblendet und können direkt angepasst oder ergänzt werden.
            </DialogContentText>
            {renderFeedbackFormFields()}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} disabled={saving || uploadingAssets}>
            Abbrechen
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving || uploadingAssets}>
            {saving || uploadingAssets
              ? (uploadingAssets ? 'Lädt Anhänge hoch...' : 'Speichert...')
              : 'Änderungen speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reviewDialog.open} onClose={closeReviewDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
              Aufarbeitung des Zwischenfalls
            </Typography>
            <Tooltip
              title={(
                <Box>
                  Die Eingaben in dieser Maske sollen beschreiben, wie der Zwischenfall zukuenftig verhindert werden kann. Sollte der Zwischenfalls entstanden sein, weil aktuelle Regelungen zu kurz greifen, soll die Frage mit "Ja" beantwortet werden und eine ausfuehrliche Aufarbeitung erfolgen.
                </Box>
              )}
            >
              <IconButton size="small" aria-label="Informationen zur Aufarbeitung">
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Benötigt der Zwischenfall eine Aufarbeitung?
            </Typography>
            <ToggleButtonGroup
              exclusive
              fullWidth
              value={resolutionState.needsFollowUp}
              onChange={(_, value) => {
                if (value) {
                  setResolutionState((prev) => ({ ...prev, needsFollowUp: value }));
                }
              }}
            >
              <ToggleButton value="yes">Ja</ToggleButton>
              <ToggleButton value="no">Nein</ToggleButton>
            </ToggleButtonGroup>

            {resolutionState.needsFollowUp === 'no' && (
              <TextField
                label="Weshalb keine Aufarbeitung notwendig ist"
                value={resolutionState.noFollowUpReason}
                onChange={(event) => setResolutionState((prev) => ({ ...prev, noFollowUpReason: event.target.value }))}
                fullWidth
                multiline
                minRows={3}
              />
            )}

            {resolutionState.needsFollowUp === 'yes' && (
              <>
                <TextField
                  label="Was die Aufarbeitung inhaltlich ist"
                  value={resolutionState.followUpSummary}
                  onChange={(event) => setResolutionState((prev) => ({ ...prev, followUpSummary: event.target.value }))}
                  fullWidth
                  multiline
                  minRows={3}
                />
                <FeedbackFileDropzone
                  label="Aufarbeitungs-PDF"
                  accept="application/pdf"
                  files={resolutionPdfFiles}
                  onFilesChange={(files) => setResolutionPdfFiles(files.slice(0, 1))}
                  buttonLabel="PDF auswählen"
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReviewDialog}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleConfirmReview}
            disabled={!isResolutionStateValid(resolutionState) || updatingStatusId === reviewDialog.entry?.id}
          >
            {updatingStatusId === reviewDialog.entry?.id ? 'Speichert...' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={completionDialog.open} onClose={closeCompletionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Zwischenfall abschließen</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <DialogContentText>
              Soll der Status wirklich auf „Abgeschlossen“ gesetzt werden?
            </DialogContentText>

            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Zusammenfassung</Typography>
            <Typography variant="body2">{completionDialog.entry?.description || '-'}</Typography>
            {Array.isArray(completionDialog.entry?.payload?.links) && completionDialog.entry.payload.links.length > 0 && (
              <Stack spacing={0.5}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>URLs</Typography>
                {completionDialog.entry.payload.links.map((link) => (
                  <Typography key={link} variant="body2">{link}</Typography>
                ))}
              </Stack>
            )}
            <Typography variant="body2">
              Bilder: {formatAssetCount(completionDialog.entry?.assets || [], 'image')} | Videos: {formatAssetCount(completionDialog.entry?.assets || [], 'video')} | Dokumente: {formatAssetCount(completionDialog.entry?.assets || [], 'document')}
            </Typography>
            <Typography variant="body2">
              Aufarbeitung benötigt: {resolutionState.needsFollowUp === 'yes' ? 'Ja' : 'Nein'}
            </Typography>
            {resolutionState.needsFollowUp === 'no' && (
              <Typography variant="body2">Begründung: {resolutionState.noFollowUpReason || '-'}</Typography>
            )}
            {resolutionState.needsFollowUp === 'yes' && (
              <Typography variant="body2">Inhalt der Aufarbeitung: {resolutionState.followUpSummary || '-'}</Typography>
            )}
            {resolutionState.needsFollowUp === 'yes' && completionDialog.entry?.resolution?.followUpPdfAssetId && (
              <Typography variant="body2">Eine PDF zur Aufarbeitung ist hinterlegt.</Typography>
            )}

            <FormControlLabel
              control={(
                <Checkbox
                  checked={resolutionState.publishToGroup}
                  onChange={(event) => setResolutionState((prev) => ({ ...prev, publishToGroup: event.target.checked }))}
                />
              )}
              label="Mitteilung an alle Höhenretter verschicken"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCompletionDialog}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleConfirmCompletion}
            disabled={!isResolutionStateValid(resolutionState) || updatingStatusId === completionDialog.entry?.id}
          >
            {updatingStatusId === completionDialog.entry?.id ? 'Speichert...' : 'Abschließen'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reopenDialog.open} onClose={closeReopenDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Zwischenfall wieder öffnen</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <DialogContentText>
              Abgeschlossene Zwischenfälle sind schreibgeschützt. Bitte begründen Sie die Wiederöffnung.
            </DialogContentText>
            <TextField
              label="Begründung für die Wiederöffnung"
              value={reopenDialog.reopenReason}
              onChange={(event) => setReopenDialog((prev) => ({ ...prev, reopenReason: event.target.value }))}
              fullWidth
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReopenDialog}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleConfirmReopen}
            disabled={!String(reopenDialog.reopenReason || '').trim() || updatingStatusId === reopenDialog.entry?.id}
          >
            {updatingStatusId === reopenDialog.entry?.id ? 'Speichert...' : 'Wieder öffnen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default RueckmeldungenPage;
